// kernel/Kernel.tsx
import { MAIN_TEXT_MODEL } from '#lib/gptBackend';
import { uploadFileToCloud, uploadImageDataToCloud } from '#lib/azure-client';
import { subscribeSharedTables } from '@/components/tables/tablesShared';
import { MIN_TIMELINE_TRACK_SPAN_MS } from '#lib/workspace-timeline';
import { buildCloudAssetEntry, upsertCloudAssets } from '#lib/cloudAssetRegistry';
import { materializeProjectGenerationLayer } from '#lib/generation-output-layer';
import React, { createContext, useContext, useState, useRef, ReactNode, useCallback, useEffect, useMemo} from 'react';
import { useAgents } from '@/kernel/useAgents';
import { toast } from 'sonner';
import { useAudioManager } from '@/kernel/useAudioManager';
import { useAnalytics } from '@/kernel/useAnalytics';
import { startSourceUsageCollector } from '#lib/source-usage-client';
import { canAccessSource } from '#lib/access/sourceAccess';
import { sourceActionContext } from '#lib/source-actions';
import { sourceUsageSurface } from '#lib/source-usage';
import { useTablesStore } from '@/components/tables/tablesStore';
import { useLayers } from '@/kernel/useLayers';
import useModels from '@/kernel/useModels';
import useGenerationTasks from '@/kernel/useGenerationTasks';
import { useProjects } from '@/kernel/useProjects';
import { useWorkspaces } from '@/kernel/useWorkspaces';
import { useUser } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';
import { pushOverlayHistory } from '#lib/overlayHistory';
import { holdWorkspaceImport, releaseWorkspaceImport } from '#lib/workspaceImportsInFlight';
import { sanitizeUserFacingError } from '#lib/failed-generation';
import { getNextActionHistory } from '#lib/actionHistoryEngine';
import { useZoom } from '@/kernel/useZoom';
import { useViewports } from '@/kernel/useViewports';
import { useCanvas } from '@/kernel/useCanvas';
import { useChat } from '@/kernel/useChat';
import useCommands from '@/kernel/useCommands';
import {
  KernelStoreApi,
  useKernelStoreApi,
  withMergedInventory,
} from '@/kernel/kernelStores';
import { useStore } from 'zustand';
import axios from 'axios';
import { CREDITS_UPDATED_EVENT, GENERATION_TASK_STATUS_EVENT, PROJECT_CREDITS_UPDATED_EVENT, USER_PREFERENCES_UPDATED_EVENT } from '#lib/socket-events';
import { UserAction, AgentObservedError, CloudAsset, CloudAssetSource, CreditsData, KernelCollapsiblePanelRef, KernelHandle, KernelState, LazyWorkerRef, OpenCvReadyMessage, Layer, LockableFile, ModalName, OpenImageCropForLockableFilesOptions, UndoableAction, UserPreferences, Workspace, EntityType, LeasingEventType, RealtimeEvent, SocketEventHandler, COMMAND_GROUPS, ENTITY_TYPES, InpaintTool, ProjectType, CollaborationMode, StudioLayer, StudioLayerDTO, StudioWorkspace, Rect, InventoryEntityType, DailyCreditUsageNotificationPreference, DailyCreditUsageAlertSaveResult, PendingInvite, getLayerWorkspaceId, isProjectLayer, normalizeLayerWorkspaceId } from '#types/types';
import { buildDailyCreditUsageAlertPreferences } from '@/kernel/dailyCreditUsageAlert';
import { defaultShortcuts } from '#lib/utils';
import { DEFAULT_STUDIO_MODEL_ID, getActiveSelectableStudioModelIds, pickFavouriteStudioImageModel } from '#lib/model-registry';
import { resolveDesignerJnnSettings } from '#lib/designer/jnn/settings';
import { clientAccessSuspension } from '#lib/access-suspension';
import { mergeKeyboardShortcutDefaults } from '#lib/keyboard-shortcuts';
import {
  LEGACY_COMPAT_USER_ACTIVITIES,
  normalizeUserActivities,
} from '#lib/user-activities';
import useVersions from '@/kernel/useVersions';
import { useSocketManager } from '@/kernel/useSocketManager';
import { useInventory } from '@/kernel/useInventory';
import { LEASING_EVENT_NAME } from '#lib/socket-events';
import { useScoringCampaigns } from '@/kernel/useScoringCampaigns';
import { useLeasingWorkflows } from '@/kernel/useLeasingWorkflows';
import { useTeams } from '@/kernel/useTeams';
import { useEntityKernelState } from '@/kernel/useEntityKernelState';
import { useWorkspaceTimeline } from '@/kernel/useWorkspaceTimeline';
import { useStudioDevelop } from '@/kernel/useStudioDevelop';
import { deepEqual } from '@/kernel/entityMerge';
import {
  areAutosavePayloadsEqual,
  buildWorkspaceVersionAutosavePayload,
  hasUnstoredLayerPixels,
  isRasterLayer,
  STUDIO_LAYER_DTO_AUTOSAVE_SCOPE,
  LAYER_PIXELS_UPLOAD_FAILED,
  mergeWorkspaceVersionAutosavePayloads,
  type StudioLayerAutosavePayload,
  type StudioWorkspaceAutosavePayload,
  type StudioWorkspaceVersionAutosavePayload,
  toLayerRuntimeOverlay,
  toStudioLayerAutosavePayload,
  toStudioLayerDtoFromRuntime,
  toStudioWorkspaceAutosavePayload,
  toStudioWorkspaceDtoFromRuntime,
  toLayerState,
  toWorkspaceState,
  toWorkspaceRuntimeOverlay,
} from '#lib/studio-runtime-dto';

/** Presence heartbeat. Below the touch route's own 5-min write throttle, so writes stay throttled. */
const USER_ACTIVITY_HEARTBEAT_MS = 3 * 60 * 1000;
const MAX_UNDO_STACK_SIZE = 10;
const STUDIO_LAYER_DTO_AUTOSAVE_DEBOUNCE_MS = 400;
const RASTER_GROUP_KEYS = new Set(['imageSrc', 'rect', 'scale', 'contentVersion']);
const STUDIO_WORKSPACE_VERSIONS_AUTOSAVE_SCOPE = 'studio_workspace_versions_dto';
const STUDIO_WORKSPACE_VERSIONS_AUTOSAVE_DEBOUNCE_MS = 300;
const DEFAULT_TOAST_MODE: NonNullable<UserPreferences['toastMode']> = 'error-only';

// Agent error pipeline: dev/runtime chatter the agent can do nothing about ,
// never mounted to the agent. Keep tight: real product errors must pass.
const AGENT_ERROR_NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Hydration failed|hydration mismatch/i,
  // Socket-layer chatter only, a bare "Connection error" prefix must NOT
  // swallow real product errors, so match the socket-specific phrasings.
  /websocket error|socket reconnect|\[SocketManager\]/i,
  /Failed to catch up after socket reconnect/i,
  // Socket.IO handshake noise: "Transport unknown" from a transport that falls back on its own.
  //
  // `\/api\/socket` used to be filtered here too, because a 400 on it recurred on every page load
  // and the agent opened every reply reporting a "refresh loop" it could not fix. It was not
  // unfixable: WE were causing it, by warming up at an address engine.io owns and answers 400 to.
  // Fixed 2026-08-13 (see pages/api/realtime-warmup.ts), so the filter comes off with it - a 400
  // on the socket path is now a real signal again, and hiding it would mean nobody notices if this
  // comes back.
  /Transport unknown/i,
  /development keys/i,
  /ChunkLoadError|Loading chunk/i,
  /AbortError|The user aborted a request|signal is aborted/i,
  /Fast Refresh/i,
];
type IdentityUpdatePayload = {
  userId?: string;
  adminEmail?: string;
  isImpersonating?: boolean;
  impersonatedUser?: { name: string; email: string } | null;
};

const parseIdentityUpdatePayload = (payload: unknown): IdentityUpdatePayload => {
  if (!isRecord(payload)) return {};
  const adminEmail = readStringField(payload, 'adminEmail');
  const isImpersonatingValue = Reflect.get(payload, 'isImpersonating');
  const impersonatedUserValue = Reflect.get(payload, 'impersonatedUser');
  const impersonatedUser =
    isRecord(impersonatedUserValue) &&
    typeof impersonatedUserValue.name === 'string' &&
    typeof impersonatedUserValue.email === 'string'
      ? { name: impersonatedUserValue.name, email: impersonatedUserValue.email }
      : null;

  return {
    userId: readStringField(payload, 'userId') || undefined,
    adminEmail: adminEmail || undefined,
    isImpersonating: typeof isImpersonatingValue === 'boolean' ? isImpersonatingValue : undefined,
    impersonatedUser,
  };
};

const resolveToastMode = (
  value: UserPreferences['toastMode'] | undefined
): NonNullable<UserPreferences['toastMode']> => {
  if (value === 'debug' || value === 'silent' || value === 'error-only') {
    return value;
  }
  return DEFAULT_TOAST_MODE;
};

const shouldDisplayToast = (
  mode: NonNullable<UserPreferences['toastMode']>,
  level: 'error' | 'non-error'
): boolean => {
  if (mode === 'silent') return false;
  if (mode === 'debug') return true;
  return level === 'error';
};

const toNormalizedStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const readStringField = (value: unknown, key: string): string => {
  if (!value || typeof value !== 'object') return '';
  const candidate = Reflect.get(value, key);
  return typeof candidate === 'string' ? candidate.trim() : '';
};

const readNumberField = (value: unknown, key: string): number | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = Reflect.get(value, key);
  if (isFiniteNumber(candidate)) return candidate;
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readBooleanField = (value: unknown, key: string): boolean | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = Reflect.get(value, key);
  return typeof candidate === 'boolean' ? candidate : null;
};

type CropPolygon = { x: number; y: number }[][];

const isCropPolygon = (value: unknown): value is CropPolygon =>
  Array.isArray(value) &&
  value.every((polygon) =>
    Array.isArray(polygon) &&
    polygon.every(
      (point) =>
        !!point &&
        typeof point === 'object' &&
        isFiniteNumber(Reflect.get(point, 'x')) &&
        isFiniteNumber(Reflect.get(point, 'y'))
    )
  );

const getOriginalFileSource = (file: File): File => {
  const originalFile = Reflect.get(file, 'originalFile');
  return originalFile instanceof File ? originalFile : file;
};

const getFileCropPolygon = (file: File): CropPolygon | null => {
  const cropPolygon = Reflect.get(file, 'cropPolygon');
  return isCropPolygon(cropPolygon) ? cropPolygon : null;
};

const setFileCropPolygon = (file: File, cropPolygon: CropPolygon | null): void => {
  Reflect.set(file, 'cropPolygon', cropPolygon);
};

const setFileOriginalSource = (file: File, originalFile: File): void => {
  Reflect.set(file, 'originalFile', originalFile);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const LOCAL_ENTITY_DELETE_MARKER = '_isDeletedLocally';

const KERNEL_CONTEXT_SYMBOL = Symbol.for('fater.kernel.react-context');

const kernelGlobalStore = globalThis as typeof globalThis & {
  [KERNEL_CONTEXT_SYMBOL]?: React.Context<KernelHandle | undefined>;
};

const Kernel = kernelGlobalStore[KERNEL_CONTEXT_SYMBOL] ?? createContext<KernelHandle | undefined>(
  undefined
);

if (!kernelGlobalStore[KERNEL_CONTEXT_SYMBOL]) {
  kernelGlobalStore[KERNEL_CONTEXT_SYMBOL] = Kernel;
}

export const useKernel = () => {
  const context = useContext(Kernel);
  if (!context) {
    throw new Error('useKernel must be used within an KernelProvider');
  }
  return context;
};


interface KernelProviderProps {
  children: ReactNode;
  isGuestMode?: boolean;
  kernelStoreOverride?: KernelStoreApi;
}

export const KernelProvider: React.FC<KernelProviderProps> = ({ 
  children, 
  isGuestMode = false,
  kernelStoreOverride
}) => {
  const kernel = useRef<KernelHandle | null>(null);
  const { isLoaded, isSignedIn } = useUser();
  
  const [kernelReady, setKernelReady] = useState(false);
  const pathname = usePathname();
  const isPortalRoute = pathname?.startsWith('/portal') ?? false;
  const isLeasingExperience = isPortalRoute;
  const isSourceHostRoute = pathname === '/';

  const inventoryManager = useInventory({ kernel });
  const scoringCampaignsManager = useScoringCampaigns({ kernel });
  const leasingWorkflowsManager = useLeasingWorkflows({ kernel });
  const teamsManager = useTeams({ kernel });
  const entityKernelStateManager = useEntityKernelState({ kernel });
  const {
    readProjectRuntimeState,
    patchProjectRuntimeState,
  } = entityKernelStateManager;

  const kernelStoreApi = useKernelStoreApi();
  const store = kernelStoreOverride || kernelStoreApi;

  const socketEventHandlersRef = useRef<Map<string, Set<SocketEventHandler>>>(new Map());
  const pendingAssetProjectIdRef = useRef<string | null>(null);
  const originalToastMethodsRef = useRef<{
    error: typeof toast.error;
    warning: typeof toast.warning;
    info: typeof toast.info;
    success: typeof toast.success;
    loading: typeof toast.loading;
  } | null>(null);

  const dispatchSocketEvent = useCallback((eventName: string, payload: unknown) => {
    if (typeof eventName !== 'string' || eventName.length === 0) {
      return;
    }
    const handlers = socketEventHandlersRef.current.get(eventName);
    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        console.error(`[Kernel] Socket handler for "${eventName}" threw an error.`, error);
      }
    });
  }, []);

  const dbUserId = useStore(store, (state) => state.userContext.dbUserId);
  const measurementOrganisationId = useStore(store, (state) => state.userContext.authenticatedOrganisationId);
  const measurementImpersonating = useStore(store, (state) => state.userContext.isImpersonating);
  const sourceUsageCollectorRef = useRef<ReturnType<typeof startSourceUsageCollector> | null>(null);
  useEffect(() => {
    if (!isSourceHostRoute) { store.setState({ portalStandaloneView: null }); return; }
    if (!isSignedIn || !dbUserId || !measurementOrganisationId || measurementImpersonating || !canAccessSource({ dbUserId })) return;
    const collector = startSourceUsageCollector(dbUserId, measurementOrganisationId,
      () => {
        const user = store.getState().userContext;
        return user.dbUserId === dbUserId && user.authenticatedOrganisationId === measurementOrganisationId && !user.isImpersonating;
      },
      () => sourceUsageSurface(store.getState(), Boolean(useTablesStore.getState().activeDocId)),
      () => sourceActionContext(store.getState()),
    );
    sourceUsageCollectorRef.current = collector;
    const unsubscribe = store.subscribe(() => collector.sample());
    const unsubscribeTables = useTablesStore.subscribe(() => collector.sample());
    return () => { unsubscribe(); unsubscribeTables(); collector.stop(); if (sourceUsageCollectorRef.current === collector) sourceUsageCollectorRef.current = null; };
  }, [isSourceHostRoute, isSignedIn, dbUserId, measurementOrganisationId, measurementImpersonating]);
  useEffect(() => subscribeSharedTables(store, kernel), []);
  const hasActiveModal = useStore(store, (state) => state.activeModal !== null);
  useEffect(() => {
    if (!hasActiveModal) return;
    return pushOverlayHistory(() => kernel.current?.closeModal());
  }, [hasActiveModal]);
  useEffect(() => {
    if (originalToastMethodsRef.current) return;

    originalToastMethodsRef.current = {
      error: toast.error.bind(toast),
      warning: toast.warning.bind(toast),
      info: toast.info.bind(toast),
      success: toast.success.bind(toast),
      loading: toast.loading.bind(toast),
    };

    const mutableToast = toast as unknown as {
      error: typeof toast.error;
      warning: typeof toast.warning;
      info: typeof toast.info;
      success: typeof toast.success;
      loading: typeof toast.loading;
    };

    const shouldShowByLevel = (level: 'error' | 'non-error'): boolean => {
      const modeFromKernel = kernel.current?.getUserPreferences()?.toastMode;
      const modeFromStore = store.getState().userPreferences.toastMode;
      return shouldDisplayToast(resolveToastMode(modeFromKernel ?? modeFromStore), level);
    };

    mutableToast.error = ((...args: Parameters<typeof toast.error>) => {
      if (!shouldShowByLevel('error')) return undefined;
      return originalToastMethodsRef.current?.error(...args);
    }) as typeof toast.error;

    mutableToast.warning = ((...args: Parameters<typeof toast.warning>) => {
      if (!shouldShowByLevel('non-error')) return undefined;
      return originalToastMethodsRef.current?.warning(...args);
    }) as typeof toast.warning;

    mutableToast.info = ((...args: Parameters<typeof toast.info>) => {
      if (!shouldShowByLevel('non-error')) return undefined;
      return originalToastMethodsRef.current?.info(...args);
    }) as typeof toast.info;

    mutableToast.success = ((...args: Parameters<typeof toast.success>) => {
      if (!shouldShowByLevel('non-error')) return undefined;
      return originalToastMethodsRef.current?.success(...args);
    }) as typeof toast.success;

    mutableToast.loading = ((...args: Parameters<typeof toast.loading>) => {
      if (!shouldShowByLevel('non-error')) return undefined;
      return originalToastMethodsRef.current?.loading(...args);
    }) as typeof toast.loading;
  }, [store]);

  useEffect(() => {
    if (!isLoaded || !kernelReady) return;
    if (kernel.current) {
      if (isSignedIn && dbUserId) {
        void kernel.current.initializeInventory();
      } else if (!isSignedIn) {
        kernel.current.clearInventory();
      }
      void kernel.current.loadUserPreferences();
    } else {
      console.log('Not ready yet.');
    }
  }, [kernel, kernelReady, isLoaded, isSignedIn, dbUserId]);

  useEffect(() => {
    const resumeStudioWrites = () => kernel.current?.resumeStudioWrites();
    // pagehide, not unload: it also fires when the page is kept in the back/forward cache, and does not bar it from there.
    const sendStudioWrites = () => { kernel.current?.sendStudioWritesAtLeave(); };
    // Sends too: the confirmation is asked only for what that send left behind.
    const confirmLeaveWithUnsentStudioWrites = (event: BeforeUnloadEvent) => {
      if (!kernel.current?.sendStudioWritesAtLeave()) return;
      event.preventDefault();
      // Chrome before 119 asks only on this.
      event.returnValue = '';
    };
    window.addEventListener('online', resumeStudioWrites);
    window.addEventListener('pagehide', sendStudioWrites);
    window.addEventListener('beforeunload', confirmLeaveWithUnsentStudioWrites);
    return () => {
      window.removeEventListener('online', resumeStudioWrites);
      window.removeEventListener('pagehide', sendStudioWrites);
      window.removeEventListener('beforeunload', confirmLeaveWithUnsentStudioWrites);
    };
  }, [kernel]);

  // Initial fetch on mount/sign-in
  useEffect(() => {
    if (!isLoaded || !kernelReady) return;
    if (isSignedIn) {
      try {
        kernel.current?.refreshOrganisationDetails(null, "essential");
        void axios.post('/api/inventory/users/activity/touch').catch((error) => {
          console.warn('[Kernel] Failed to touch user activity:', error);
        });
        // Heartbeat, not just a sign-in stamp. Touched once per session, `lastActive` only ever
        // meant "opened a tab", which made every presence read across the app wrong. The route
        // throttles writes to one per 5 min server-side, so this interval is a no-op write while
        // the tab sits idle.
        //
        // `heartbeat: true` is load-bearing: it puts the route on a cheap path that does one
        // indexed write and nothing else. A presence ping is not a business mutation, so it must
        // not pay for a GenerationLog COUNT, must not write a TimelineEvent, must not broadcast an
        // org-wide socket mutation, and must not evaluate the activity email. Without the split,
        // one admin view costs EVERY signed-in user those four things every few minutes.
        //
        // Note this pauses on `visibilityState`, which is not idleness: a window that is merely
        // unfocused or on a second monitor still reports 'visible'. So presence here means "a tab
        // is open and on screen", not "someone is at the keyboard", and the feed's active count
        // should be read that way.
        const touchActivity = () => {
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
          // The response carries the server's suspension verdict, and applying it is the whole
          // reason this tab does not have to be reloaded when an account is restored or cut off.
          void axios.post('/api/inventory/users/activity/touch', { heartbeat: true })
            .then((response) => {
              const verdict = response?.data?.accessSuspension;
              if (!verdict) return;
              kernel.current?.setUserContext((prev: any) => ({ ...prev, accessSuspension: verdict }));
            })
            .catch(() => {});
        };
        const heartbeat = setInterval(touchActivity, USER_ACTIVITY_HEARTBEAT_MS);
        return () => clearInterval(heartbeat);
      } catch (error) {
        kernel.current?.setUserContext({ dbUserId: null, organisationId: null, isImpersonating: false, impersonatedUser: null, trueUser: null, isFaterAdmin: false, isFaterOrgMember: false, isOrganisationAdmin: false });
      }
    } else {
      store.setState({
          userContext: {  dbUserId: null, organisationId: null, isImpersonating: false, impersonatedUser: null, trueUser: null, isFaterAdmin: false, isFaterOrgMember: false, isOrganisationAdmin: false }
      });
    }
  }, [isLoaded, store, isSignedIn, kernelReady]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let longPressTimer: NodeJS.Timeout | null = null;
    let lastContextMenuTime = 0;
    let startPos = { x: 0, y: 0 };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      const { clientX, clientY, screenX, screenY } = touch;
      startPos = { x: clientX, y: clientY };
      if (longPressTimer) clearTimeout(longPressTimer);
      longPressTimer = setTimeout(() => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const event = new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX,
          clientY,
          screenX,
          screenY,
          button: 2,
          buttons: 2,
        });
        target.dispatchEvent(event);
        lastContextMenuTime = Date.now();
        longPressTimer = null;
      }, 600);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!longPressTimer) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startPos.x;
      const dy = touch.clientY - startPos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 15) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const handleGlobalClickCapture = (e: MouseEvent) => {
      if (Date.now() - lastContextMenuTime < 500) {
        e.preventDefault();
        e.stopPropagation();
        lastContextMenuTime = 0; 
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('click', handleGlobalClickCapture, true);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('click', handleGlobalClickCapture, true);
      if (longPressTimer) clearTimeout(longPressTimer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const setAltPressed = (next: boolean) => {
      const state = store.getState();
      if (state.isAltKeyPressed === next) return;
      state.setIsAltKeyPressed(next);
    };
    const setShiftPressed = (next: boolean) => {
      const state = store.getState();
      if (state.isShiftKeyPressed === next) return;
      state.setIsShiftKeyPressed(next);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-factory-tour-guide]')) return;
      if (event.key === 'Alt') {
        event.preventDefault();
        event.stopPropagation();
        setAltPressed(true);
        return;
      }
      if (event.key === 'Shift') {
        setShiftPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-factory-tour-guide]')) {
        if (event.key === 'Alt') setAltPressed(false);
        if (event.key === 'Shift') setShiftPressed(false);
        return;
      }
      if (event.key === 'Alt') {
        event.preventDefault();
        event.stopPropagation();
        setAltPressed(false);
        return;
      }
      if (event.key === 'Shift') {
        setShiftPressed(false);
        return;
      }
    };

    const handleBlur = () => {
      setAltPressed(false);
      setShiftPressed(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        setAltPressed(false);
        setShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setAltPressed(false);
      setShiftPressed(false);
    };
  }, [store]);

  const subscribeToSocketEvent = useCallback(
    (eventName: string, handler: SocketEventHandler) => {
      if (typeof eventName !== 'string' || eventName.trim().length === 0 || typeof handler !== 'function') {
        return () => {};
      }

      const normalizedEvent = eventName.trim();
      let handlers = socketEventHandlersRef.current.get(normalizedEvent);
      if (!handlers) {
        handlers = new Set();
        socketEventHandlersRef.current.set(normalizedEvent, handlers);
      }

      handlers.add(handler);

      return () => {
        const existing = socketEventHandlersRef.current.get(normalizedEvent);
        if (!existing) {
          return;
        }
        existing.delete(handler);
        if (existing.size === 0) {
          socketEventHandlersRef.current.delete(normalizedEvent);
        }
      };
    },
    []
  );

  const { 
    setActiveModal, setModalPayload, setIsGuestMode,
    setUndoStack, setRedoStack, setActiveChatSessionId, // Destructure to exclude from ...setters
    ...setters 
  } = store.getState();

  const studioLeftPanelRef = useRef<KernelCollapsiblePanelRef | null>(null);
  const studioRightPanelRef = useRef<KernelCollapsiblePanelRef | null>(null);

  const setLeftPanelRef = useCallback<KernelHandle['setLeftPanelRef']>((panelRef) => {
    studioLeftPanelRef.current = panelRef;
  }, []);

  const setRightPanelRef = useCallback<KernelHandle['setRightPanelRef']>((panelRef) => {
    studioRightPanelRef.current = panelRef;
  }, []);

  const toggleLeftPanel = useCallback<KernelHandle['toggleLeftPanel']>((userGesture) => {
    const leftPanel = studioLeftPanelRef.current;
    if (!leftPanel) return;

    kernel.current?.logUserAction('Left Sidebar Toggled by Button');
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) {
      const rightPanel = studioRightPanelRef.current;
      const leftIsCollapsed = leftPanel.isCurrentlyCollapsed();
      if (leftIsCollapsed && rightPanel && !rightPanel.isCurrentlyCollapsed()) {
        rightPanel.toggleCollapse();
      }
    }
    leftPanel.toggleCollapse(undefined, userGesture);
  }, [kernel]);

  const toggleRightPanel = useCallback<KernelHandle['toggleRightPanel']>((userGesture) => {
    const rightPanel = studioRightPanelRef.current;
    if (!rightPanel) return;

    kernel.current?.logUserAction('Right Sidebar Toggled by Button');
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) {
      const leftPanel = studioLeftPanelRef.current;
      const rightIsCollapsed = rightPanel.isCurrentlyCollapsed();
      if (rightIsCollapsed && leftPanel && !leftPanel.isCurrentlyCollapsed()) {
        leftPanel.toggleCollapse();
      }
    }
    rightPanel.toggleCollapse(undefined, userGesture);
  }, [kernel]);

  const openLeftPanel = useCallback<KernelHandle['openLeftPanel']>(() => {
    if (studioLeftPanelRef.current?.isCurrentlyCollapsed()) kernel.current?.toggleLeftPanel();
  }, []);

  const openRightPanel = useCallback<KernelHandle['openRightPanel']>((forceOpenToWidth) => {
    const rightPanel = studioRightPanelRef.current;
    if (!rightPanel || !rightPanel.isCurrentlyCollapsed()) return;

    kernel.current?.logUserAction('Right Sidebar Opened Programmatically');
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) {
      const leftPanel = studioLeftPanelRef.current;
      if (leftPanel && !leftPanel.isCurrentlyCollapsed()) {
        leftPanel.toggleCollapse();
      }
    }
    rightPanel.toggleCollapse(forceOpenToWidth);
  }, [kernel]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const handleFirstInteraction = () => {
      if (!kernel.current?.getAudioEnabled()) {
        console.log('[KernelProvider] Audio enabled after user interaction.');
        kernel.current?.setAudioEnabled(true);
      }
      document.removeEventListener('pointerdown', handleFirstInteraction);
    };

    document.addEventListener('pointerdown', handleFirstInteraction);
    return () => {
      document.removeEventListener('pointerdown', handleFirstInteraction);
    };
  }, [kernel]);

  useEffect(() => {
    const mode = isLeasingExperience ? 'commander' : undefined;
    store.getState().setAgentModeOverride(mode);
  }, [store, isLeasingExperience]);

  const registerCloudAssets = useCallback((urls: (string | null | undefined)[], source: CloudAssetSource) => {
    if (!kernel.current) return;
    const nowIso = new Date().toISOString();
    const assets: CloudAsset[] = urls
      .map(url => buildCloudAssetEntry(url, source, nowIso))
      .filter((asset): asset is CloudAsset => asset !== null);

    if (assets.length === 0) return;
    const existingRegistry = kernel.current.getCloudAssetRegistry() ?? [];
    const updatedRegistry = upsertCloudAssets(existingRegistry, assets);
    kernel.current.setCloudAssetRegistry(() => updatedRegistry);
  }, [kernel]);

  const toggleReferenceImageLock = useCallback((id: string) => {
    const projectId = kernel.current?.resolveActiveSessionProjectId?.() || '';
    if (!projectId) return;
    let found = false;
    kernel.current?.setExtraFiles(projectId, prev => {
      const newFiles = prev.map(f => {
        if (f.id === id) {
          found = true;
          return { ...f, locked: !f.locked };
        }
        return f;
      });
      return found ? newFiles : prev;
    });

    if (!found) {
      kernel.current?.setVideoLastImages(projectId, prev => {
        return prev.map(f => {
          if (f.id === id) {
            return { ...f, locked: !f.locked };
          }
          return f;
        });
      });
    }
  }, [kernel]);

  const assetLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAssetLoadingFailsafe = useCallback(() => {
    if (assetLoadTimeoutRef.current) {
      clearTimeout(assetLoadTimeoutRef.current);
      assetLoadTimeoutRef.current = null;
    }
  }, []);

  const handleAssetLoadingTimeout = useCallback(() => {
    const pendingUrls = kernel.current?.getPendingAssetUrls();
    if ((pendingUrls?.size || 0) > 0) {
      console.warn('[Kernel Failsafe] The following essential assets timed out after 60s:', Array.from(pendingUrls || []));
      toast.warning('Some project assets may be missing due to a slow connection.');
      kernel.current?.setPendingAssetUrls(() => new Set());
    }
  }, [kernel]);

  const startAssetLoadingFailsafe = useCallback((timeoutMs = 60000) => {
    clearAssetLoadingFailsafe();
    assetLoadTimeoutRef.current = setTimeout(() => {
      handleAssetLoadingTimeout();
    }, timeoutMs);
  }, [clearAssetLoadingFailsafe, handleAssetLoadingTimeout]);

  const reportAssetLoaded = useCallback((url: string) => {
    if (!url) return;
    kernel.current?.setPendingAssetUrls(prevSet => {
      if (!prevSet.has(url)) {
        return prevSet;
      }
      const updated = new Set(prevSet);
      updated.delete(url);
      return updated;
    });
  }, []);

  const initializeVisualCriticalPath = useCallback((projectId: string, layers: Layer[]) => {
    const normalizedProjectId = projectId.trim();
    const manifest = new Set<string>();
    const collect = (value?: string | null) => {
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (trimmed.length === 0) return;
      manifest.add(trimmed);
    };

    const registerLayer = (layer?: Layer) => {
      if (!layer || !layer.visible || !layer.layerWorkspaceId) return;
      if (layer.type === 'video') {
        collect(layer.videoSrc);
      } else {
        collect(layer.imageSrc);
      }
    };

    if (Array.isArray(layers)) {
      layers.forEach(registerLayer);
    }

    kernel.current?.setPendingAssetUrls(() => new Set(manifest));
    pendingAssetProjectIdRef.current = normalizedProjectId || null;

    if (manifest.size === 0) {
      if (normalizedProjectId) {
        kernel.current?.patchProjectRuntimeState(normalizedProjectId, { essentialAssetsReady: true });
      }
      clearAssetLoadingFailsafe();
      return;
    }

    if (normalizedProjectId) {
      kernel.current?.patchProjectRuntimeState(normalizedProjectId, { essentialAssetsReady: false });
    }
    startAssetLoadingFailsafe();
  }, [clearAssetLoadingFailsafe, startAssetLoadingFailsafe]);

  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      const manifestChanged = state.pendingAssetUrls !== prevState.pendingAssetUrls;
      if (!manifestChanged) {
        return;
      }
      const hadPending = prevState.pendingAssetUrls.size > 0;
      const hasPending = state.pendingAssetUrls.size > 0;
      if (!hadPending && !hasPending) {
        return;
      }
      const projectId = pendingAssetProjectIdRef.current?.trim() ?? '';
      const essentialAssetsReady = projectId
        ? kernel.current?.readProjectRuntimeState(projectId).essentialAssetsReady === true
        : false;
      if (state.pendingAssetUrls.size === 0 && !essentialAssetsReady) {
        console.log('[Kernel] All essential assets are loaded or have timed out. Firing ready signal.');
        clearAssetLoadingFailsafe();
        if (projectId) {
          kernel.current?.patchProjectRuntimeState(projectId, { essentialAssetsReady: true });
        }
        pendingAssetProjectIdRef.current = null;
      }
    });
    return unsubscribe;
  }, [store, clearAssetLoadingFailsafe]);

  // A message queued while the agent worked is sent once the session has been idle for a moment: the
  // pause lets a run's own continuation start first, and that continuation reads the message itself.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = store.subscribe(() => {
      const sessionId = kernel.current?.getActiveChatSessionId();
      if (!sessionId || !kernel.current?.readEntityRuntimeState('ChatSession', sessionId).queuedUserMessage) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; void kernel.current?.flushQueuedChatMessage(sessionId); }, 1500);
    });
    return () => { unsubscribe(); if (timer) clearTimeout(timer); };
  }, [store]);

  useEffect(() => {
    return () => {
      clearAssetLoadingFailsafe();
    };
  }, [clearAssetLoadingFailsafe]);
  const consecutiveRevertCounterRef = useRef(0);
  const analytics = useAnalytics();
  const pendingTrackActionRef = useRef<UserAction | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const blendWorkerRef = useRef<Worker | null>(null);
  const generationPostprocessWorkerRef = useRef<Worker | null>(null);
  const buriedLayersWorkerRef = useRef<Worker | null>(null);
  // Created on first alignment only: OpenCV is an 11 MB chunk plus a WASM heap, and alignment is rare.
  const opencvWorkerRef = useRef<LazyWorkerRef>({ current: null, created: false });
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const pendingLayerPromisesRef = useRef<Map<string, { resolve: (layer: Layer) => void; reject: (reason?: any) => void; }>>(new Map());

  useEffect(() => {
    setIsGuestMode(isGuestMode);
  }, [setIsGuestMode, isGuestMode]);
  
  const openModal = useCallback((modalName: Exclude<ModalName, null>, payload?: any) => {
    setModalPayload(payload);
    setActiveModal(modalName);
  }, [setModalPayload, setActiveModal]);

  const openImageCropForLockableFiles = useCallback((
    lockableFiles: LockableFile[],
    imageSetter: (files: LockableFile[] | ((prevFiles: LockableFile[]) => LockableFile[])) => void,
    index: number,
    sourceRect: DOMRect | null,
    options?: OpenImageCropForLockableFilesOptions
  ) => {
    const lockableFile = lockableFiles[index];
    if (!lockableFile) return;

    const onConfirm = (
      { file, polygons }: { file?: File; polygons: { x: number; y: number }[][] },
      originalIndex: number
    ) => {
      if (!file) {
        imageSetter((prevFiles) => {
          const newFiles = [...prevFiles];
          const fileToUpdate = newFiles[originalIndex];
          if (fileToUpdate) {
            setFileCropPolygon(fileToUpdate.file, polygons);
          }
          return newFiles;
        });
        return;
      }

      imageSetter((prevFiles) => {
        const newFiles = [...prevFiles];
        const fileToReplace = prevFiles[originalIndex];
        if (fileToReplace) {
          const originalFileSource = getOriginalFileSource(fileToReplace.file);
          setFileOriginalSource(file, originalFileSource);
          setFileCropPolygon(file, polygons);
          newFiles[originalIndex] = {
            ...fileToReplace,
            file,
          };
        }
        return newFiles;
      });
    };

    const originalFile = getOriginalFileSource(lockableFile.file);
    const initialPolygon = getFileCropPolygon(lockableFile.file);

    openModal('ImageCrop', {
      imageFile: originalFile,
      alternateImageFile: options?.alternateImageFile,
      imageIndex: index,
      onConfirm,
      initialPolygon,
      sourceRect,
      instruction: options?.instruction ?? null,
      outputMode: options?.outputMode,
      autoConfirmOnPreview: options?.autoConfirmOnPreview,
    });
  }, [openModal]);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setModalPayload(null);
  }, [setActiveModal, setModalPayload]);


  // Agent error pipeline (docs/specs/agent-error-pipeline.md): every runtime
  // error the user's session surfaces is recorded here and mounted to the
  // agent each turn until resolved or escalated. Dev/runtime chatter the agent
  // can do nothing about is filtered out.
  const recordAgentObservedError = useCallback((
    source: AgentObservedError['source'],
    message: string,
    detail?: string
  ) => {
    const normalized = (message || '').trim();
    if (!normalized) return;
    if (AGENT_ERROR_NOISE_PATTERNS.some((pattern) => pattern.test(normalized))) return;
    const now = Date.now();
    store.getState().setAgentErrorLedger((prev: AgentObservedError[]) => {
      // Same message within the dedup window folds into the existing entry.
      const existing = prev.find(
        (entry) => entry.message === normalized.slice(0, 400) && entry.status !== 'RESOLVED' && now - entry.ts < 120_000
      );
      if (existing) {
        // ts tracks the LAST occurrence, not the first. It is what decides whether this error is
        // still happening (see RUNTIME_ERROR_STALE_AFTER_MS), and an error that recurs every
        // minute must not age out just because it started an hour ago.
        return prev.map((entry) =>
          entry.id === existing.id ? { ...entry, count: entry.count + 1, ts: now } : entry
        );
      }
      const entry: AgentObservedError = {
        id: uuidv4(),
        ts: now,
        source,
        message: normalized.slice(0, 400),
        detail: detail ? detail.slice(0, 600) : undefined,
        status: 'NEW',
        count: 1,
      };
      const next = [...prev, entry];
      return next.length > 20 ? next.slice(next.length - 20) : next;
    });
  }, [store]);

  useEffect(() => {
    const onWindowError = (event: ErrorEvent) => {
      recordAgentObservedError(
        'window',
        event.message || 'Unknown window error',
        event.error instanceof Error ? event.error.stack ?? undefined : undefined
      );
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      const message =
        reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection';
      recordAgentObservedError(
        'promise',
        message,
        reason instanceof Error ? reason.stack ?? undefined : undefined
      );
    };
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [recordAgentObservedError]);

  const handleError = useCallback<KernelHandle['handleError']>((message, options) => {
    const { logToConsole = true, ...toastOptions } = options ?? {};
    // The console keeps the RAW text: that is where a supplier's own wording is worth having.
    if (logToConsole) console.error(`Kernel Error: ${message}`);
    // Everything that leaves this function is sanitized. The toast is the obvious one; the agent
    // record matters just as much, because an agent handed "Top up your balance at fal.ai" will
    // relay it to the user in its own words and route around the gate below.
    const safeMessage = sanitizeUserFacingError(message);
    recordAgentObservedError('kernel', safeMessage);
    toast.error(safeMessage, toastOptions);
  }, [recordAgentObservedError]);

  const executeAction = useCallback((action: Omit<UndoableAction, 'id'>) => {
    const newAction: UndoableAction = {
      ...action,
      id: uuidv4(),
    };

    // Perform the action
    Promise.resolve(newAction.redo()).catch(err => {
      console.error(`[Action] Error executing redo for "${newAction.description}":`, err);
      handleError('Action failed.');
    });

    // Add it to the undo stack and clear the redo stack
    setUndoStack(prev => {
      const next = [...prev, newAction];
      return next.length > MAX_UNDO_STACK_SIZE
        ? next.slice(next.length - MAX_UNDO_STACK_SIZE)
        : next;
    });
    setRedoStack([]); // Any new action clears the redo history
    console.log(`[Action] Executed: ${newAction.description}`);
  }, [setUndoStack, setRedoStack, handleError]);

  const undoLastAction = useCallback(() => {
    const undoStack = kernel.current?.getUndoStack() || [];
    if (undoStack.length === 0) {
      console.log('[Undo] Stack is empty.');
      return;
    }

    const newUndoStack = [...undoStack];
    const actionToUndo = newUndoStack.pop();

    if (actionToUndo) {
      kernel.current?.playUISound('UNDO', { volume: 0.15 });
      console.log(`[Undo] Executing: ${actionToUndo.description}`);
      kernel.current?.logUserAction('Undo', { description: actionToUndo.description });
      Promise.resolve(actionToUndo.undo()).catch(err => {
        console.error(`[Undo] Error executing undo for "${actionToUndo.description}":`, err);
        handleError('Undo action failed.');
      });

      setUndoStack(newUndoStack);
      setRedoStack(prev => {
        const next = [actionToUndo, ...prev];
        return next.length > MAX_UNDO_STACK_SIZE
          ? next.slice(0, MAX_UNDO_STACK_SIZE)
          : next;
      });
    }
  }, [setUndoStack, setRedoStack, handleError]);

  const redoLastAction = useCallback(() => {
    const redoStack = kernel.current?.getRedoStack() || [];
    if (redoStack.length === 0) {
      console.log('[Redo] Stack is empty.');
      return;
    }

    const newRedoStack = [...redoStack];
    const actionToRedo = newRedoStack.shift(); // Pop from the front

    if (actionToRedo) {
      kernel.current?.playUISound('REDO', { volume: 0.15 });
      console.log(`[Redo] Executing: ${actionToRedo.description}`);
      kernel.current?.logUserAction('Redo', { description: actionToRedo.description });
      Promise.resolve(actionToRedo.redo()).catch(err => {
        console.error(`[Redo] Error executing redo for "${actionToRedo.description}":`, err);
        handleError('Redo action failed.');
      });

      setRedoStack(newRedoStack);
      setUndoStack(prev => {
        const next = [...prev, actionToRedo];
        return next.length > MAX_UNDO_STACK_SIZE
          ? next.slice(next.length - MAX_UNDO_STACK_SIZE)
          : next;
      });
    }
  }, [setUndoStack, setRedoStack, handleError]);

  useEffect(() => {
    blendWorkerRef.current = new Worker(new URL('../workers/layer-raster.worker.ts', import.meta.url), { type: 'module' });
    generationPostprocessWorkerRef.current = new Worker(new URL('../workers/generation-postprocess.worker.ts', import.meta.url), { type: 'module' });
    buriedLayersWorkerRef.current = new Worker(new URL('../workers/buried-layers.worker.ts', import.meta.url), { type: 'module' });
    return () => {
      if (blendWorkerRef.current) {
        blendWorkerRef.current.terminate();
        blendWorkerRef.current = null;
      }
      if (generationPostprocessWorkerRef.current) {
        generationPostprocessWorkerRef.current.terminate();
        generationPostprocessWorkerRef.current = null;
      }
      buriedLayersWorkerRef.current?.terminate();
      buriedLayersWorkerRef.current = null;
      opencvWorkerRef.current.current?.terminate();
      opencvWorkerRef.current.current = null;
      // Fast Refresh reruns this effect on the same ref: a teardown is not a dead worker, so allow recreation.
      opencvWorkerRef.current.created = false;
    };
  }, []);

  const createOpenCvWorker = useCallback(() => {
    const worker = new Worker(new URL('../workers/opencv.worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', (event: MessageEvent<Partial<OpenCvReadyMessage> | undefined>) => {
      if (event.data?.type !== 'ready' || kernel.current?.getUserPreferences()?.performanceHudEnabled !== true) return;
      console.log('[Perf][opencvWorker] ready', { version: event.data.version, initMs: event.data.initMs });
    });
    return worker;
  }, []);


  const audioManager = useAudioManager({ kernel});
  const layerManager = useLayers({ kernel });
  const workspaceManager = useWorkspaces({ kernel });
  const modelManager = useModels({ kernel });
  const taskManager = useGenerationTasks({ kernel });
  const chatManager = useChat({ kernel });
  const { connectSocket, disconnectSocket, getSocketConnectionId, socketRef} = useSocketManager({ kernel });

  const commands = useCommands({ kernel });
  const { runGeneratedScriptLogic, parseSlashCommand, execute, executeSlashCommandDirectly } = commands;

  useEffect(() => {
    if (!kernelReady) return;
    const unsubscribe = subscribeToSocketEvent(GENERATION_TASK_STATUS_EVENT, (payload) => {
      kernel.current?.handleGenerationTaskStatusSocketEvent(payload);
    });
    return () => {
      unsubscribe?.();
    };
  }, [kernelReady]);

  useEffect(() => {
    if (!kernelReady || !kernel.current) return;

    const handleIdentityUpdate = (payload: unknown) => {
      const p = parseIdentityUpdatePayload(payload);
      const userContext = kernel.current?.getUserContext();

      if (p?.adminEmail && userContext?.trueUser?.email && p.adminEmail === userContext.trueUser.email) {
        console.log('[Kernel] Identity update triggered by self (Admin). Updating context...');
        
        // Manually update the user context to reflect the change immediately
        if (p.isImpersonating !== undefined) {
            const newContext = { ...userContext };
            newContext.isImpersonating = p.isImpersonating;
            newContext.impersonatedUser = p.isImpersonating && p.impersonatedUser ? p.impersonatedUser : null;
            store.setState({ userContext: newContext });
        }

        kernel.current?.refreshOrganisationDetails(null, "essential");
        return;
      }
      // The event is broadcast to the whole organisation; an id-less refresh resets the selected
      // organisation to the home one, moving the socket off the room this user's events use.
      if (p?.userId && p.userId === userContext?.dbUserId) {
        kernel.current?.refreshOrganisationDetails(null, "essential");
      }
    };

    const handlePrefsUpdate = (payload: any) => {
      if (payload?.userId === kernel.current?.getUserContext().dbUserId) {
        console.log('[Kernel] User preferences updated in another tab. Syncing...');
        kernel.current?.loadUserPreferences();
      }
    };

    const unsubscribeIdentity = kernel.current.subscribeToSocketEvent('user-identity-updated', handleIdentityUpdate);
    const unsubscribePrefs = kernel.current.subscribeToSocketEvent(USER_PREFERENCES_UPDATED_EVENT, handlePrefsUpdate);

    return () => {
      unsubscribeIdentity?.();
      unsubscribePrefs?.();
    };
  }, [kernelReady, store, kernel]);


  const userPreferences = useStore(store, (state) => state.userPreferences);
  const organisationDetails = useStore(store, (state) => {
    const orgId = state.userContext.organisationId;
    return orgId ? state.inventory.Organisation.registry[orgId] : null;
  });
  
  useEffect(() => {
    const userPref = userPreferences.language;
    const orgPref = organisationDetails?.favoriteLanguage;
    const effective = userPref || orgPref || 'ENGLISH';
    const code = effective === 'FRENCH' ? 'fr' : 'en';
    const currentLang = kernel.current?.getLanguage();
    
    if (code !== currentLang) {
      kernel.current?.setLanguage(code);
    }
  }, [userPreferences.language, organisationDetails?.favoriteLanguage, kernel]);

  useEffect(() => {
    if (!isLoaded || !kernelReady) {
      return;
    }

    if (!isSignedIn) {
      kernel.current?.setOrganisationCredits(null);
      kernel.current?.setOrganisationCreditsError(null);
      kernel.current?.setIsOrganisationCreditsLoading(false);
      kernel.current?.setIsOrganisationMember(false);
      return;
    }

    if (!kernel.current) {
      return;
    }

    let isCancelled = false;
    const runWithKernel = (fn: (handle: KernelHandle) => void) => {
      if (isCancelled) {
        return;
      }
      const handle = kernel.current;
      if (!handle) {
        return;
      }
      fn(handle);
    };

    const fetchOrganisationCredits = async () => {
      runWithKernel((handle) => {
        handle.setIsOrganisationCreditsLoading(true);
        handle.setOrganisationCreditsError(null);
      });

      try {
        const response = await axios.get<CreditsData>('/api/organisation/credits', {
          withCredentials: true,
        });

        runWithKernel((handle) => {
          handle.setOrganisationCredits(response.data);
          handle.setIsOrganisationMember(true);
        });
      } catch (error) {
        const axiosError = axios.isAxiosError(error) ? error : null;
        if (axiosError?.response?.status === 403) {
          runWithKernel((handle) => {
            handle.setOrganisationCredits(null);
            handle.setIsOrganisationMember(false);
            handle.setOrganisationCreditsError(null);
          });
        } else {
          console.error('[Kernel] Failed to load organisation credits', error);
          runWithKernel((handle) => {
            handle.setOrganisationCredits(null);
            handle.setOrganisationCreditsError(
              axiosError?.message || 'Failed to load organisation credits'
            );
          });
        }
      } finally {
        runWithKernel((handle) => {
          handle.setIsOrganisationCreditsLoading(false);
        });
      }
    };

    fetchOrganisationCredits();

    return () => {
      isCancelled = true;
    };
  }, [isLoaded, isSignedIn, kernelReady]);

  const organisationId = useStore(store, (state) => state.userContext.organisationId);

  useEffect(() => {
    if (!isLoaded || !kernelReady) {
      return;
    }
    if (!isSignedIn || !organisationId) {
      disconnectSocket();
      return;
    }

    connectSocket();
    return () => {
      disconnectSocket();
    };
  }, [connectSocket, disconnectSocket, isLoaded, isSignedIn, organisationId, kernelReady]);

  // Effect to handle late arrival of organisationId for socket room joining
  useEffect(() => {
    const socket = socketRef?.current;
    if (socket && socket.connected && organisationId) {
      console.log(`[Kernel] Organisation ID available (${organisationId}). Re-emitting join-room.`);
      const lastTimestamp = kernel.current?.getSocketLastEventTimestamp();
      const payload: { organisationId?: string; lastEventTimestamp?: string } = {
        organisationId,
      };
      if (lastTimestamp) {
        payload.lastEventTimestamp = lastTimestamp;
      }
      socket.emit('join-room', payload);
    }
  }, [organisationId, socketRef]);

  useEffect(() => {
    if (!kernelReady) {
      return;
    }

    const handleCreditsUpdate = (payload: unknown) => {
      if (!isRecord(payload)) {
        return;
      }

      const remainingCredits = readNumberField(payload, 'remainingCredits');
      if (remainingCredits === null) {
        return;
      }
      const rawOverageDisplayText = Reflect.get(payload, 'overageDisplayText');
      const overageDisplayText =
        typeof rawOverageDisplayText === 'string' || rawOverageDisplayText === null
          ? rawOverageDisplayText
          : null;

      const organisationRemainingCredits = readNumberField(payload, 'organisationRemainingCredits');
      const organisationPoolRemainingCredits = readNumberField(payload, 'organisationPoolRemainingCredits');
      const teamRemainingCredits = readNumberField(payload, 'teamRemainingCredits');
      const totalTeamRemainingCredits = readNumberField(payload, 'totalTeamRemainingCredits');
      const projectedTotalCreditsSpent = readNumberField(payload, 'projectedTotalCreditsSpent');

      const nextData: CreditsData = {
        remainingCredits,
        ...(organisationRemainingCredits !== null ? { organisationRemainingCredits } : {}),
        ...(organisationPoolRemainingCredits !== null ? { organisationPoolRemainingCredits } : {}),
        ...(teamRemainingCredits !== null ? { teamRemainingCredits } : {}),
        ...(totalTeamRemainingCredits !== null ? { totalTeamRemainingCredits } : {}),
        ...(projectedTotalCreditsSpent !== null ? { projectedTotalCreditsSpent } : {}),
        allowOverage: (() => {
          const raw = Reflect.get(payload, 'allowOverage');
          return typeof raw === 'boolean' ? raw : Boolean(raw);
        })(),
        overageDisplayText,
        isRestricted: readBooleanField(payload, 'isRestricted') === true,
      };

      kernel.current?.setOrganisationCredits((current) => {
        if (
          current &&
          current.remainingCredits === nextData.remainingCredits &&
          current.organisationRemainingCredits === nextData.organisationRemainingCredits &&
          current.organisationPoolRemainingCredits === nextData.organisationPoolRemainingCredits &&
          current.teamRemainingCredits === nextData.teamRemainingCredits &&
          current.totalTeamRemainingCredits === nextData.totalTeamRemainingCredits &&
          current.projectedTotalCreditsSpent === nextData.projectedTotalCreditsSpent &&
          current.allowOverage === nextData.allowOverage &&
          current.overageDisplayText === nextData.overageDisplayText
        ) {
          return current;
        }
        return nextData;
      });

      kernel.current?.setIsOrganisationCreditsLoading(false);
      kernel.current?.setOrganisationCreditsError(null);
      kernel.current?.setIsOrganisationMember(true);
    };

    const unsubscribe = subscribeToSocketEvent(CREDITS_UPDATED_EVENT, handleCreditsUpdate);
    return () => {
      unsubscribe?.();
    };
  }, [subscribeToSocketEvent, kernelReady]);

  useEffect(() => {
    if (!kernelReady) {
      return;
    }

    const handleProjectCreditsUpdate = (payload: unknown) => {
      if (!isRecord(payload)) {
        return;
      }
      const projectId = readStringField(payload, 'projectId');
      const totalCreditsSpent = readNumberField(payload, 'totalCreditsSpent');
      if (!projectId || totalCreditsSpent === null) {
        return;
      }
      // An UPDATE on an absent registry entry is dropped by applyInventoryEvents, which is what we
      // want: a project nobody has open must not be materialised from a credit counter alone.
      kernel.current?.applyInventoryEvents([
        {
          entityType: 'Project',
          entityId: projectId,
          eventType: 'UPDATE',
          payload: { totalCreditsSpent },
        },
      ]);
    };

    const unsubscribe = subscribeToSocketEvent(PROJECT_CREDITS_UPDATED_EVENT, handleProjectCreditsUpdate);
    return () => {
      unsubscribe?.();
    };
  }, [subscribeToSocketEvent, kernelReady]);

  const logUserAction = useCallback((actionName: string, payload?: Record<string, any>) => {
    kernel.current?.setActionHistory(currentHistory => {
      const newHistory = getNextActionHistory(currentHistory, actionName, payload);
      const lastActionInNewHistory = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;

      if (!lastActionInNewHistory) {
        return newHistory;
      }

      const previouslyPendingAction = pendingTrackActionRef.current;

      if (newHistory.length > currentHistory.length) {
        if (previouslyPendingAction) {
          analytics.track(previouslyPendingAction.name, previouslyPendingAction.payload);
        }
      }

      pendingTrackActionRef.current = lastActionInNewHistory;
      return newHistory;
    });
  }, [analytics]);

  const beginSourceAction = useCallback<KernelHandle['beginSourceAction']>((input) => {
    const complete = sourceUsageCollectorRef.current?.beginAction(input);
    if (complete) kernel.current?.logUserAction(`Source ${input.action}`, { origin: input.origin });
    return complete;
  }, []);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const userPreferenceSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const userPreferenceWritesByOwnerRef = useRef(new Map<string, Promise<void>>());
  const userPreferenceRecoveryRef = useRef<{ ownerId: string; generation: number; promise: Promise<void> } | null>(null);
  const userPreferencePendingPatchRef = useRef<Partial<UserPreferences>>({});
  const userPreferenceOwnerRef = useRef<string | null>(null);
  const userPreferenceGenerationRef = useRef(0);
  const userPreferenceLoadSequenceRef = useRef(0);
  const userPreferenceMutationRef = useRef(0);

  const favouriteModelSettledRef = useRef(false);
  // Once per load, on the first preferences loaded: the pin and the hidden models live there, and a later run would undo the user's own choice.
  const preselectFavouriteStudioModel = useCallback(() => {
    const k = kernel.current;
    if (!k || favouriteModelSettledRef.current) return;
    favouriteModelSettledRef.current = true;
    try {
      const userContext = k.getUserContext();
      const userPreferences = k.getUserPreferences();
      if (k.getDiffusionModel() !== DEFAULT_STUDIO_MODEL_ID) return;
      const selectableModelIds = getActiveSelectableStudioModelIds({
        accessScope: userContext.permissions?.accessScope,
        organisationRestricted: k.getOrganisationCredits()?.isRestricted ?? null,
        withheldModelFamilies: clientAccessSuspension(userContext.accessSuspension, userContext.organisationId, userContext.teamId).withheldModelFamilies,
        modelVisibility: userPreferences.modelVisibility || {},
      });
      if (userPreferences.lockSelectedModelForAgent === true) {
        // A lock pins the model the user chose, not whatever loaded first.
        const locked = userPreferences.lockedStudioModel;
        if (locked && locked !== DEFAULT_STUDIO_MODEL_ID && selectableModelIds.includes(locked)) k.selectDiffusionModel(locked);
        return;
      }
      const favouriteModelId = pickFavouriteStudioImageModel(userContext.studioModelUsage, selectableModelIds);
      if (!favouriteModelId || favouriteModelId === DEFAULT_STUDIO_MODEL_ID) return;
      k.selectDiffusionModel(favouriteModelId);
      k.logUserAction('Favourite Model Preselected', { modelId: favouriteModelId });
    } catch (error) {
      // The preferences load calls this step and must still persist its own shortcut reset.
      console.error('[Kernel] Failed to preselect the favourite model:', error);
      k.handleError('Failed to preselect your favourite model.');
    }
  }, []);

  const loadUserPreferences = useCallback(async () => {
    const loadSequence = ++userPreferenceLoadSequenceRef.current;
    const ownerId = kernel.current?.getUserContext().dbUserId ?? null;
    if (userPreferenceOwnerRef.current !== ownerId) {
      userPreferenceOwnerRef.current = ownerId;
      userPreferenceGenerationRef.current += 1;
      userPreferencePendingPatchRef.current = {};
      kernel.current?.setUserPreferences(store.getInitialState().userPreferences);
      store.setState({ userPreferencesOwnerId: null });
    }
    if (!ownerId) return;
    const generation = userPreferenceGenerationRef.current;
    const mutationRevision = userPreferenceMutationRef.current;
    console.log('Loading Prefs')
    try {
      await userPreferenceWritesByOwnerRef.current.get(ownerId);
      if (userPreferenceLoadSequenceRef.current !== loadSequence || userPreferenceMutationRef.current !== mutationRevision) return;
      if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
      const response = await axios.get<UserPreferences>('/api/user/preferences');
      if (userPreferenceLoadSequenceRef.current !== loadSequence || userPreferenceMutationRef.current !== mutationRevision) return;
      if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
      // A signed-out or restarting server answers 200 with an HTML page; spreading that string stored one key per
      // character (29,593 on one account) and the next save wrote them back. Only a JSON object is preferences.
      if (!response.data || typeof response.data !== 'object' || Array.isArray(response.data)) throw new Error('Preferences response is not a JSON object.');
      const loadedPrefsRaw = response.data;
      const { activeMaskTheme: _legacyMaskTheme, backdropTheme: _legacyBackdropTheme, ...loadedPrefs } =
        loadedPrefsRaw as UserPreferences & { activeMaskTheme?: unknown; backdropTheme?: unknown };

      // --- SHORTCUT RESET LOGIC ---
      // This block detects if the user has shortcuts in the old `event.code` format.
      // If so, it flushes all their custom shortcuts and restores the application defaults.
      let shortcutsToApply = loadedPrefs.shortcuts || {};
      let needsReset = false;
      if (shortcutsToApply && Object.keys(shortcutsToApply).some(key => key.includes('+Key'))) {
        console.warn('[Shortcut Reset] Old shortcut format detected. Resetting all user shortcuts to application defaults.');
        shortcutsToApply = defaultShortcuts;
        needsReset = true;
      }
      // --- END RESET LOGIC ---

      // Add newly shipped defaults while preserving remaps and explicit empty-string tombstones.
      const finalShortcuts = mergeKeyboardShortcutDefaults(shortcutsToApply);

      const defaultDisabledCommands = [
        ...COMMAND_GROUPS.GA_CHANGE
      ];

      const finalPrefs: UserPreferences = {
        ...loadedPrefs,
        slidesWelcomeVersion: typeof loadedPrefs.slidesWelcomeVersion === 'string' ? loadedPrefs.slidesWelcomeVersion : null,
        spectacleModeDefault: loadedPrefs.spectacleModeDefault ?? true,
        performanceHudEnabled: loadedPrefs.performanceHudEnabled ?? false,
        disabledCommands: loadedPrefs.disabledCommands ?? defaultDisabledCommands,
        lockSelectedModelForAgent: loadedPrefs.lockSelectedModelForAgent ?? false,
        // Absent in the DB means never dismissed: normalize to false (shown) here, distinct
        // from the pre-hydration default of true (hidden) set in kernelStores.ts.
        // Absent in the DB means never used: normalize to [] so the effect in ChatTab.tsx can
        // tell "not hydrated yet" (undefined) apart from "hydrated, genuinely empty" ([]).
        // Array.isArray guards the untyped JSON column at this network trust boundary.
        recentAssistantModelIds: Array.isArray(loadedPrefs.recentAssistantModelIds)
          ? loadedPrefs.recentAssistantModelIds
          : [],
        // Same sentinel contract: the Portal sweep forces no re-title while this is undefined.
        retitledMasterSessionIds: Array.isArray(loadedPrefs.retitledMasterSessionIds)
          ? loadedPrefs.retitledMasterSessionIds
          : [],
        // Absent in the DB means nothing pinned yet: normalize to [] so a toggle is allowed
        // (undefined stays the "not hydrated yet" sentinel that blocks writes, see
        // lib/masterSessionPins.ts). Array.isArray guards the untyped JSON column.
        pinnedMasterSessionIds: Array.isArray(loadedPrefs.pinnedMasterSessionIds)
          ? loadedPrefs.pinnedMasterSessionIds
          : [],
        shortcuts: finalShortcuts,
        assistantModel: loadedPrefs.assistantModel || MAIN_TEXT_MODEL,
        modelVisibility: loadedPrefs.modelVisibility || {},
        autoPromoteOnGenerate: loadedPrefs.autoPromoteOnGenerate ?? true,
        imageVariationCount: loadedPrefs.imageVariationCount ?? 3,
        workspaceVersionPreviewFocusTopVisibleLayerRect: loadedPrefs.workspaceVersionPreviewFocusTopVisibleLayerRect ?? true,
        appearanceTheme: loadedPrefs.appearanceTheme ?? 'DARK',
        actSilently: loadedPrefs.actSilently ?? false,
        toastMode: resolveToastMode(loadedPrefs.toastMode),
        activities: normalizeUserActivities(loadedPrefs.activities, {
          fallback: LEGACY_COMPAT_USER_ACTIVITIES,
        }),
        notificationPreferences: {
          ...loadedPrefs.notificationPreferences,
          dailyCreditUsageAlert: {
            enabled: false,
            thresholdCredits: 50,
            repeatStepCredits: 25,
            timezone: null,
            ...loadedPrefs.notificationPreferences?.dailyCreditUsageAlert,
          },
        },
      };

      const firstHydration = store.getState().userPreferencesOwnerId !== ownerId;
      kernel.current?.setUserPreferences(finalPrefs);
      store.setState({ userPreferencesOwnerId: ownerId });
      kernel.current?.setSpectacleMode(finalPrefs.spectacleModeDefault ?? true);
      // Once per account: a reload must not reset a budget the user picked on the chip since.
      if (firstHydration) kernel.current?.setDesignerBudget(resolveDesignerJnnSettings(finalPrefs.designerJnn).defaultBudget);
      preselectFavouriteStudioModel();

      // If we performed a reset, save the updated (default) shortcuts back to the DB immediately.
      // This makes the reset permanent and prevents this logic from running again for this user.
      if (needsReset) {
        await kernel.current?.saveUserPreferences(finalPrefs);
      }

    } catch (error) {
      if (userPreferenceLoadSequenceRef.current !== loadSequence || userPreferenceMutationRef.current !== mutationRevision) return;
      if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
      console.error('[Kernel] Failed to load user preferences:', error);
      if (store.getState().userPreferencesOwnerId === ownerId) return;
      // Unknown preferences may hide a pin: this load preselects nothing, and a later reload must not either.
      favouriteModelSettledRef.current = true;

      const defaultDisabledCommands = [
        ...COMMAND_GROUPS.GA_CHANGE
      ];

      kernel.current?.setUserPreferences({
        spectacleModeDefault: true,
        performanceHudEnabled: false,
        disabledCommands: defaultDisabledCommands,
        lockSelectedModelForAgent: false,
        // Fail-safe hidden: a failed load must not flash the explainer at a user who already
        // dismissed it. recentAssistantModelIds is deliberately left absent (undefined) here;
        // persistRecentUnifiedModels treats that sentinel as "unknown truth" and never writes.
        shortcuts: defaultShortcuts,
        assistantModel: MAIN_TEXT_MODEL,
        workspaceVersionPreviewFocusTopVisibleLayerRect: true,
        appearanceTheme: 'DARK',
        actSilently: false,
        toastMode: DEFAULT_TOAST_MODE,
        activities: [...LEGACY_COMPAT_USER_ACTIVITIES],
        notificationPreferences: {
          dailyCreditUsageAlert: {
            enabled: false,
            thresholdCredits: 50,
            repeatStepCredits: 25,
            timezone: null,
          },
        },
      });
      kernel.current?.setSpectacleMode(true); // Fallback for current session on error
    }
  }, [preselectFavouriteStudioModel]);


  const saveUserPreferences = useCallback(async (prefsToSave: UserPreferences) => {
    const ownerId = kernel.current?.getUserContext().dbUserId;
    if (!ownerId || userPreferenceOwnerRef.current !== ownerId || store.getState().userPreferencesOwnerId !== ownerId) return;
    const generation = userPreferenceGenerationRef.current;
    userPreferenceMutationRef.current += 1;
    const queuedSave = userPreferenceSaveQueueRef.current.then(async () => {
      if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
      try {
        const { activeMaskTheme: _legacyMaskTheme, backdropTheme: _legacyBackdropTheme, ...prefsWithoutLegacyMaskTheme } =
          prefsToSave as UserPreferences & { activeMaskTheme?: unknown; backdropTheme?: unknown };
        const normalizedPrefsToSave: UserPreferences = {
          ...prefsWithoutLegacyMaskTheme,
        };
        const response = await axios.put<UserPreferences>('/api/user/preferences', normalizedPrefsToSave);
        if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
        const savedPrefs = response.data || {};

        const defaultDisabledCommands = [
          ...COMMAND_GROUPS.GA_CHANGE
        ];

        kernel.current?.setUserPreferences({
          // Spread first, server echo last: any key this function does not know about survives the
          // round trip untouched. This used to be an explicit allowlist, and every preference added
          // to UserPreferences without a line here was silently ERASED from memory on the next full
          // save (the List loop toggle switched itself off whenever the user touched the models
          // popover beside it; visiblePortfolioIds emptied the admin's portfolio filter). The
          // normalizations below still apply on top -- they are defaults, not a membership list.
          ...normalizedPrefsToSave,
          ...savedPrefs,
          assistantGlobalPrompt: savedPrefs.assistantGlobalPrompt,
          spectacleModeDefault: savedPrefs.spectacleModeDefault,
          performanceHudEnabled: savedPrefs.performanceHudEnabled ?? false,
          disabledCommands: savedPrefs.disabledCommands ?? defaultDisabledCommands,
          lockSelectedModelForAgent: savedPrefs.lockSelectedModelForAgent ?? false,
          recentAssistantModelIds: savedPrefs.recentAssistantModelIds ?? prefsToSave.recentAssistantModelIds ?? [],
          retitledMasterSessionIds: savedPrefs.retitledMasterSessionIds ?? prefsToSave.retitledMasterSessionIds ?? [],
          sourceRecentAssetIds: savedPrefs.sourceRecentAssetIds ?? prefsToSave.sourceRecentAssetIds,
          pinnedMasterSessionIds: savedPrefs.pinnedMasterSessionIds ?? prefsToSave.pinnedMasterSessionIds ?? [],
          shortcuts: savedPrefs.shortcuts || defaultShortcuts,
          assistantModel: savedPrefs.assistantModel || MAIN_TEXT_MODEL,
          defaultCollaborationMode: savedPrefs.defaultCollaborationMode,
          sourceAgentMode: savedPrefs.sourceAgentMode,
          toolDefaultModels: savedPrefs.toolDefaultModels ?? {},
          modelVisibility: savedPrefs.modelVisibility || {},
          autoPromoteOnGenerate: savedPrefs.autoPromoteOnGenerate,
          imageVariationCount: savedPrefs.imageVariationCount ?? 3,
          workspaceVersionPreviewFocusTopVisibleLayerRect: savedPrefs.workspaceVersionPreviewFocusTopVisibleLayerRect ?? true,
          language: savedPrefs.language,
          appearanceTheme: savedPrefs.appearanceTheme ?? prefsToSave.appearanceTheme ?? 'DARK',
          commanderOrganisationId: savedPrefs.commanderOrganisationId ?? null,
          actSilently: savedPrefs.actSilently ?? false,
          lastImageExportSettings: savedPrefs.lastImageExportSettings,
          toastMode: resolveToastMode(savedPrefs.toastMode),
          activities: normalizeUserActivities(savedPrefs.activities, {
            fallback: normalizeUserActivities(
              prefsToSave.activities,
              { fallback: LEGACY_COMPAT_USER_ACTIVITIES }
            ),
          }),
          notificationPreferences: savedPrefs.notificationPreferences ?? prefsToSave.notificationPreferences,
          ...userPreferencePendingPatchRef.current,
        });

        if (normalizedPrefsToSave.spectacleModeDefault !== undefined) {
          kernel.current?.setSpectacleMode(kernel.current.getUserPreferences().spectacleModeDefault !== false);
        }
      } catch (error) {
        if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
        console.error('[Kernel] Failed to save user preferences:', error);
        kernel.current?.handleError('Failed to save preferences.');
      }
    });
    userPreferenceSaveQueueRef.current = queuedSave;
    userPreferenceWritesByOwnerRef.current.set(ownerId, queuedSave);
    try {
      await queuedSave;
    } finally {
      if (userPreferenceWritesByOwnerRef.current.get(ownerId) === queuedSave) userPreferenceWritesByOwnerRef.current.delete(ownerId);
    }
  }, []);


  const updateAndSaveUserPreference = useCallback(async <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    if (!kernel.current) return;
    const ownerId = kernel.current.getUserContext().dbUserId;
    if (!ownerId || userPreferenceOwnerRef.current !== ownerId) return;
    const generation = userPreferenceGenerationRef.current;
    const wasUnhydrated = store.getState().userPreferencesOwnerId !== ownerId;
    let recovery = userPreferenceRecoveryRef.current;
    if (wasUnhydrated || (recovery?.ownerId === ownerId && recovery.generation === generation)) {
      if (!recovery || recovery.ownerId !== ownerId || recovery.generation !== generation) {
        recovery = { ownerId, generation, promise: kernel.current.loadUserPreferences() };
        userPreferenceRecoveryRef.current = recovery;
      }
      try {
        await recovery.promise;
      } finally {
        if (userPreferenceRecoveryRef.current === recovery) userPreferenceRecoveryRef.current = null;
      }
      if (userPreferenceGenerationRef.current !== generation || kernel.current.getUserContext().dbUserId !== ownerId) return;
      if (store.getState().userPreferencesOwnerId !== ownerId) {
        kernel.current.handleError('Failed to load preferences. Please retry.');
        return;
      }
      if (wasUnhydrated && value !== null && typeof value === 'object') {
        kernel.current.handleError('Preferences were reloaded. Please apply this setting again.');
        return;
      }
    }
    userPreferenceMutationRef.current += 1;

    const currentPrefs = kernel.current.getUserPreferences();
    const previousValue = currentPrefs[key];
    const newPrefs = { ...currentPrefs, [key]: value };

    // Optimistic UI update
    kernel.current.setUserPreferences(newPrefs);
    userPreferencePendingPatchRef.current = {
      ...userPreferencePendingPatchRef.current,
      [key]: value,
    };
    if (key === 'spectacleModeDefault' && typeof value === 'boolean') {
      kernel.current.setSpectacleMode(value);
    }

    const queuedSave = userPreferenceSaveQueueRef.current.then(async () => {
      if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
      try {
        // Always send the freshest optimistic snapshot. Queue ordering prevents a
        // slower, older full-preferences PUT from overwriting a newer setting.
        const latestPreferences = kernel.current?.getUserPreferences();
        if (!latestPreferences) return;
        const sentPatch = { ...userPreferencePendingPatchRef.current };
        if (Object.keys(sentPatch).length === 0) return;
        await axios.put<UserPreferences>('/api/user/preferences', {
          ...latestPreferences,
          ...sentPatch,
        });
        if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
        const remainingPatch = { ...userPreferencePendingPatchRef.current };
        (Object.keys(sentPatch) as Array<keyof UserPreferences>).forEach((patchKey) => {
          const pendingValue = remainingPatch[patchKey];
          const sentValue = sentPatch[patchKey];
          const stillMatchesSentValue = Object.is(pendingValue, sentValue)
            || JSON.stringify(pendingValue) === JSON.stringify(sentValue);
          if (stillMatchesSentValue) delete remainingPatch[patchKey];
        });
        userPreferencePendingPatchRef.current = remainingPatch;
      } catch (error) {
        if (userPreferenceGenerationRef.current !== generation || kernel.current?.getUserContext().dbUserId !== ownerId) return;
        console.error(`[Kernel] Failed to auto-save preference '${String(key)}':`, error);
        const pendingValue = userPreferencePendingPatchRef.current[key];
        const failedValueIsStillPending = Object.is(pendingValue, value)
          || JSON.stringify(pendingValue) === JSON.stringify(value);
        if (failedValueIsStillPending) {
          const remainingPatch = { ...userPreferencePendingPatchRef.current };
          delete remainingPatch[key];
          userPreferencePendingPatchRef.current = remainingPatch;
        }
        const latestPreferences = kernel.current?.getUserPreferences();
        if (latestPreferences) {
          const latestValue = latestPreferences[key];
          const valueIsStillCurrent = Object.is(latestValue, value)
            || JSON.stringify(latestValue) === JSON.stringify(value);
          if (valueIsStillCurrent) {
            kernel.current?.setUserPreferences({ ...latestPreferences, [key]: previousValue });
            if (key === 'spectacleModeDefault') {
              kernel.current?.setSpectacleMode(previousValue !== false);
            }
          }
        }
        kernel.current?.handleError(`Failed to save setting for ${String(key)}.`);
      }
    });

    userPreferenceSaveQueueRef.current = queuedSave;
    userPreferenceWritesByOwnerRef.current.set(ownerId, queuedSave);
    try {
      await queuedSave;
    } finally {
      if (userPreferenceWritesByOwnerRef.current.get(ownerId) === queuedSave) userPreferenceWritesByOwnerRef.current.delete(ownerId);
    }
  }, []);

  /**
   * Persists the daily credit usage alert: normalizes and validates the requested alert, merges it
   * into the sibling notification preferences, saves, then re-reads to confirm the write landed.
   * Returns the outcome instead of touching any component state, and never rejects: the caller
   * drives a saving flag on it, so a throw here would leave the panel locked for good.
   */
  const saveDailyCreditUsageAlert = useCallback(async (
    alert: DailyCreditUsageNotificationPreference,
    fallbackTimezone: string
  ): Promise<DailyCreditUsageAlertSaveResult> => {
    try {
      const currentPreferences: UserPreferences = kernel.current?.getUserPreferences() ?? store.getState().userPreferences;
      const built = buildDailyCreditUsageAlertPreferences(alert, currentPreferences, fallbackTimezone);
      if (!built.ok) return built;

      await kernel.current?.updateAndSaveUserPreference('notificationPreferences', built.notificationPreferences);
      const savedAlert = kernel.current?.getUserPreferences().notificationPreferences?.dailyCreditUsageAlert;
      if (JSON.stringify(savedAlert) !== JSON.stringify(built.notificationPreferences.dailyCreditUsageAlert)) {
        return { ok: false, error: 'The notification preference could not be saved.' };
      }
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error instanceof Error ? error.message : 'Failed to save notification preferences.' };
    }
  }, [store]);

  const handleNotEnoughCreditsError = useCallback(() => {
    toast.info("You have run out of credits. Please contact your administrator for assistance.");
  }, []);

  const triggerGenerate = useCallback((source: 'prompt_enter_key' | 'generate_button' = 'prompt_enter_key') => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    const projectId = kernel.current?.resolveActiveSessionProjectId?.() || '';
    const UIWorkspaceId = kernel.current?.getProjectUIWorkspaceId(projectId);
    if(!diffusionModel || !UIWorkspaceId) {
      console.error('No diff model or no workspace id.')
      return;
    }
    const params = kernel.current?.getModelParams()[diffusionModel];

    if (kernel.current?.getSpectacleMode()) 
      kernel.current?.playUISound('GENERATE_START', { volume: 0.4 });
    
    kernel.current?.logUserAction('Generation Initiated', {
      source,
      model_id: diffusionModel,
      num_outputs: kernel.current?.getLocalNumOutputs(projectId),
      prompt: params?.prompt,
      prompt_length: params?.prompt?.length ?? 0
    });
    kernel.current?.prepareWorkspaceForGeneration(UIWorkspaceId);
    kernel.current?.runModel(UIWorkspaceId);
  }, [kernel]);

  const refreshOrganisationDetails = useCallback(async (orgId: string | null, mode: 'essential' | 'full' | 'members' = 'essential') => {
    if (!kernel.current) return;
    
    try {
      let apiUrl = orgId
        ? `/api/organisation/details?orgId=${orgId}&mode=${mode}`
        : `/api/organisation/details?mode=${mode}`;

      const response = await axios.get(apiUrl);
      const data = response.data;

      const realId = data.organisationDetails?.id;
      if (!realId) return;
      
      store.setState(state => {
        const prevOrgSlice = state.inventory.Organisation;
        const existingOrg = prevOrgSlice.registry[realId];
        
        const mergedOrg: any = {
          ...(existingOrg || {}),
          ...data.organisationDetails,
          plans: data.activePlans !== undefined ? data.activePlans : existingOrg?.plans,
          stats: data.stats !== undefined ? data.stats : existingOrg?.stats,
        };

        const nextOrgRegistry = { ...prevOrgSlice.registry, [realId]: mergedOrg };

        let nextUserRegistry = state.inventory.User.registry;

        if (data.members && data.members.length > 0) {
          nextUserRegistry = { ...nextUserRegistry };
          data.members.forEach((m: any) => {
            const existingUser = nextUserRegistry[m.id] || {};
            const mergedUser = { ...existingUser, ...m, organisationId: realId };
            nextUserRegistry[m.id] = mergedUser;
          });
        }
        
        return { 
          inventory: {
            ...state.inventory,
            Organisation: {
              ...prevOrgSlice,
              registry: nextOrgRegistry,
            },
            User: {
              ...state.inventory.User,
              registry: nextUserRegistry,
            }
          }
        };
      });

      if (!orgId) {
        if (data.userContext) kernel.current.setUserContext(data.userContext);
      }
    } catch (error) {
      console.error('[Kernel] Failed to refresh organisation details', error);
    }
  }, [kernel, store]);

  const fetchOrganisationPendingInvites = useCallback<KernelHandle['fetchOrganisationPendingInvites']>(async (organisationId) => {
    const response = await fetch(`/api/organisation/invite/fetch?organisationId=${encodeURIComponent(organisationId)}`);
    const responseText = await response.text();
    let data: unknown = null;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
      } catch {
        data = null;
      }
    }
    if (!response.ok) {
      const errorMessage = data && typeof data === 'object'
        ? Reflect.get(data, 'message') ?? Reflect.get(data, 'error')
        : null;
      throw new Error(
        typeof errorMessage === 'string'
          ? errorMessage
          : `Failed to fetch invites (HTTP ${response.status}).`
      );
    }
    return Array.isArray(data) ? data as PendingInvite[] : [];
  }, []);

  const saveOrganisationSettings = useCallback<KernelHandle['saveOrganisationSettings']>(async (
    organisationId,
    settings,
    refreshTargetOrgId
  ) => {
    const response = await fetch('/api/organisation/members', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'updateOrganisationSettings',
        organisationId,
        defaultCollaborationMode: settings.defaultCollaborationMode,
        favoriteLanguage: settings.favoriteLanguage,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.message || `Error ${response.status}`);
    }
    await refreshOrganisationDetails(refreshTargetOrgId, 'essential');
  }, [refreshOrganisationDetails]);

  const inviteOrganisationMember = useCallback<KernelHandle['inviteOrganisationMember']>(async (organisationId, email) => {
    const response = await fetch('/api/organisation/invite/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'invite', email, organisationId }),
    });
    const result = await response.json();
    if (!response.ok) { throw new Error(result.message || `Error ${response.status}`); }
    return result;
  }, []);

  const createOrganisationMemberDirectly = useCallback<KernelHandle['createOrganisationMemberDirectly']>(async (
    organisationId,
    member,
    refreshTargetOrgId
  ) => {
    const response = await fetch('/api/organisation/members', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'createDirect', ...member, organisationId }),
    });
    const result = await response.json();
    if (!response.ok) { throw new Error(result.message || `Error ${response.status}`); }
    await refreshOrganisationDetails(refreshTargetOrgId, 'full');
    return result;
  }, [refreshOrganisationDetails]);

  const resendOrganisationInvite = useCallback<KernelHandle['resendOrganisationInvite']>(async (organisationId, email) => {
    const response = await fetch('/api/organisation/invite/resend', {
      method: 'POST',
      body: JSON.stringify({ email, organisationId }),
    });
    // Cette route répond en texte brut sur ses refus (`new NextResponse('…')`), pas en JSON :
    // `.json()` lèverait alors avant même le test de `response.ok`, et l'utilisateur lirait une
    // erreur de parsing au lieu du motif du refus.
    const result = await response.json().catch(() => null);
    if (!response.ok || !result) { throw new Error(result?.message || `Error ${response.status}`); }
    return result;
  }, []);

  const deleteOrganisationInvite = useCallback<KernelHandle['deleteOrganisationInvite']>(async (inviteId) => {
    const response = await fetch(`/api/organisation/invite/delete/${inviteId}`, {
      method: 'DELETE',
    });
    // Cette route répond en texte brut sur ses refus (`new NextResponse('…')`), pas en JSON :
    // `.json()` levait alors avant même le test de `response.ok`, et l'utilisateur lisait une
    // erreur de parsing au lieu du motif du refus.
    const result = await response.json().catch(() => null);
    if (!response.ok || !result) {
      throw new Error(result?.message || `Error ${response.status}`);
    }
    return result;
  }, []);

  const uploadOrganisationLogo = useCallback<KernelHandle['uploadOrganisationLogo']>(async (organisationId, file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please select a PNG, JPG, GIF, SVG, or WebP image.');
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File is too large. Maximum size is 5MB.');
    }

    const formData = new FormData();
    formData.append('action', 'uploadLogo');
    formData.append('organisationId', organisationId); // API uses this ID
    formData.append('logo', file);

    const response = await fetch('/api/organisation/members', { method: 'POST', body: formData });
    const result = await response.json();
    if (!response.ok) { throw new Error(result.message || `Upload failed with status ${response.status}`); }
    if (result.organisation) {
      await kernel.current?.syncEntity('Organisation', organisationId);
    }
  }, [kernel]);

  // Switch the whole app onto one organisation: refresh the org list, decide whether the
  // selection is the neutral "Fater" scope (admin, not impersonating), move the user context,
  // then re-pull every org-scoped inventory so no stale row survives the switch. The admin
  // preference is only persisted when the caller asks for it.
  const applyHomeOrganisationScope = useCallback<KernelHandle['applyHomeOrganisationScope']>(async (
    orgId,
    options
  ) => {
    const k = kernel.current;
    if (!k) return;

    const normalizedOrgId = typeof orgId === 'string' ? orgId.trim() : '';
    if (!normalizedOrgId) return;

    // Snapshot the admin flags before the refresh, like the component closure this
    // orchestration came from did (they cannot change mid-switch anyway).
    const userContext = k.getUserContext();

    await k.refreshInventory('Organisation', undefined, undefined, { force: true });
    const selectedOrganisation = k.getInventory().Organisation.registry[normalizedOrgId];
    const isFaterNeutralSelection = Boolean(
      userContext?.isFaterAdmin &&
      !userContext?.isImpersonating &&
      typeof selectedOrganisation?.name === 'string' &&
      selectedOrganisation.name.trim().toLowerCase() === 'fater'
    );
    const nextContextOrganisationId = isFaterNeutralSelection ? null : normalizedOrgId;

    const currentOrgId = k.getUserContext().organisationId ?? null;
    const shouldSwitchContext = currentOrgId !== nextContextOrganisationId;
    if (!shouldSwitchContext) {
      if (
        !options?.persistAdminPreference ||
        !userContext?.isFaterAdmin ||
        userContext?.isImpersonating
      ) {
        return;
      }
    }

    if (shouldSwitchContext) {
      k.setUserContext((prev: any) => ({ ...prev, organisationId: nextContextOrganisationId }));
      if (nextContextOrganisationId) {
        await k.refreshOrganisationDetails(nextContextOrganisationId, 'essential');
      }
    }

    const scopedEntities: InventoryEntityType[] = ['Property', 'Unit', 'Deal', 'Lead', 'Project', 'User'];
    await Promise.all(scopedEntities.map((entity) => k.refreshInventory(entity, undefined, undefined, { force: true })));

    if (
      options?.persistAdminPreference &&
      userContext?.isFaterAdmin &&
      !userContext?.isImpersonating
    ) {
      const nextCommanderOrganisationId = isFaterNeutralSelection ? null : normalizedOrgId;
      const currentPrefs = k.getUserPreferences?.() ?? null;
      if (currentPrefs && currentPrefs.commanderOrganisationId !== nextCommanderOrganisationId) {
        void k.updateAndSaveUserPreference('commanderOrganisationId', nextCommanderOrganisationId);
      }
    }
  }, [kernel]);

  const sendUserTeamReport = useCallback<KernelHandle['sendUserTeamReport']>(async (targetUserId, options) => {
    const normalizedUserId = typeof targetUserId === 'string' ? targetUserId.trim() : '';
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }

    const payload: { overrideRecipientEmail?: string } = {};
    if (typeof options?.overrideRecipientEmail === 'string') {
      const normalizedEmail = options.overrideRecipientEmail.trim().toLowerCase();
      if (normalizedEmail) {
        payload.overrideRecipientEmail = normalizedEmail;
      }
    }

    const response = await axios.post(
      `/api/inventory/users/${normalizedUserId}/reportTeam`,
      payload,
    );
    return response.data as {
      success: boolean;
      managerId: string;
      recipientEmail: string;
      sent: number;
      failed: number;
      emailId: string | null;
    };
  }, []);


  const projectManager = useProjects({ kernel });
  const agentManager = useAgents({ kernel });
  const zoomManager = useZoom({ kernel });
  const viewportManager = useViewports({ kernel });
  const canvasManager = useCanvas({ kernel });
  const versionManager = useVersions({ kernel });
  const workspaceTimelineManager = useWorkspaceTimeline({ kernel });
  const studioDevelopManager = useStudioDevelop({ kernel });

  const addFilesAsLayers = useCallback(async (
    files: FileList,
    dropTargetArea: string,
    worldPos?: { x: number, y: number },
    options?: {
      timelineDrop?: {
        workspaceId: string;
        startMs: number;
        insertBeforeLayerId?: string | null;
      };
      projectId?: string;
    }
  ) => {
    if (!files || files.length === 0 || !kernel.current) return;

    const readKernel = (): KernelHandle => {
      if (!kernel.current) throw new Error('Kernel unavailable.');
      return kernel.current;
    };

    const activeSessionId = readKernel().getActiveChatSessionId();
    const requestedProjectId = options?.projectId ?? (activeSessionId ? readKernel().getLastProjectId(activeSessionId) : null);
    const originalProjectId = typeof requestedProjectId === 'string' ? requestedProjectId.trim() : '';
    // No Studio project, nothing to import: this function is the Studio import and stays out of
    // portal surfaces entirely. Chat drops without a project are attached upstream, in
    // useGlobalDropHandler, before this is ever called.
    if (!originalProjectId) return;

    // Capture the import destination before any upload starts. The completion path must not
    // re-read the current view after a Canvas/List switch.
    const capturedImportViewMode = readKernel().getStudioViewMode();

    const toFiniteMs = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const normalizedTimelineDrop = (() => {
      const drop = options?.timelineDrop;
      if (!drop || typeof drop !== 'object') return null;
      const workspaceId = typeof drop.workspaceId === 'string' ? drop.workspaceId.trim() : '';
      const startMs = toFiniteMs(drop.startMs);
      if (!workspaceId || startMs === null) return null;
      const insertBeforeLayerId =
        typeof drop.insertBeforeLayerId === 'string' && drop.insertBeforeLayerId.trim().length > 0
          ? drop.insertBeforeLayerId.trim()
          : null;
      return {
        workspaceId,
        startMs: Math.max(0, Math.round(startMs)),
        insertBeforeLayerId,
      };
    })();

    const applyTimelineDropPlacement = (workspaceId: string, layerId: string) => {
      if (!normalizedTimelineDrop || normalizedTimelineDrop.workspaceId !== workspaceId) return;

      const startMs = normalizedTimelineDrop.startMs;
      const insertBeforeLayerId = normalizedTimelineDrop.insertBeforeLayerId;

      readKernel().setLayer(layerId, (existingLayer) => {
        const existingSourceInMs = Math.max(0, toFiniteMs(existingLayer.timelineSourceInMs) ?? 0);
        const existingEndMs = toFiniteMs(existingLayer.timelineEndMs);
        const isImageLikeLayer =
          existingLayer.type !== 'video' &&
          existingLayer.type !== 'audio';
        return {
          ...existingLayer,
          timelineStartMs: startMs,
          // Image-like layers dropped onto the timeline default to a one-frame
          // lifetime (T1 = T0 + 1 frame); media layers keep their probed end.
          timelineEndMs:
            isImageLikeLayer
              ? startMs + MIN_TIMELINE_TRACK_SPAN_MS
              : (existingEndMs !== null && existingEndMs > startMs ? Math.round(existingEndMs) : null),
          timelineSourceInMs: existingSourceInMs,
        };
      });

      if (!insertBeforeLayerId) return;

      const targetWorkspace = readKernel().getWorkspace(workspaceId);
      const targetProjectId =
        typeof targetWorkspace?.projectId === 'string' && targetWorkspace.projectId.trim().length > 0
          ? targetWorkspace.projectId.trim()
          : originalProjectId;

      // Reuse standard SceneTab/LayerRow ordering path (kernel.reorderLayer)
      // so renderOrder resequencing and workspace-layer reconciliation stay centralized.
      readKernel().reorderLayer(targetProjectId, layerId, insertBeforeLayerId, false);
    };

    const listProjectWorkspaces = (): Workspace[] => {
      const inventory = readKernel().getInventory();
      const project = inventory.Project.registry[originalProjectId];
      const workspaceIds = Array.isArray(project?.workspaceIds)
        ? project.workspaceIds.filter(
            (workspaceId): workspaceId is string =>
              typeof workspaceId === 'string' && workspaceId.trim().length > 0
          )
        : [];
      return workspaceIds
        .map((workspaceId) => inventory.StudioWorkspace.registry[workspaceId])
        .filter((workspace): workspace is StudioWorkspace => Boolean(workspace))
        .map(toWorkspaceState);
    };

    // Every layer this drop creates, in creation order, with the file it came
    // from. A List project shows workspace VERSIONS as tiles, never raw layers,
    // so an imported file that stays a layer is invisible: it uploads, and
    // nothing appears. These are published as versions once the drop has landed.
    const importedLayers: { layerId: string; sourceFile: File }[] = [];

    // Records the layer AND flashes it in the UI. Both effects belong to "a
    // layer just arrived from outside", so they share one call site; renaming
    // this to a pulse-only helper would silently drop List imports.
    const registerImportedLayer = (layerId: string, sourceFile: File) => {
      const normalizedLayerId = typeof layerId === 'string' ? layerId.trim() : '';
      if (!normalizedLayerId) return;
      importedLayers.push({ layerId: normalizedLayerId, sourceFile });
      const existingLayer = readKernel().getLayerRegistry()[normalizedLayerId];
      if (!existingLayer) {
        readKernel().patchEntityRuntimeState('StudioLayer', normalizedLayerId, {
          pulseToken: Date.now(),
        });
        return;
      }
      readKernel().setLayer(normalizedLayerId, (existingLayer) => ({
        ...existingLayer,
        pulseToken: Date.now(),
      }));
    };

    // The feedback ends when the drop ends, for every project kind: a List output is only
    // published after the last file, so a per-file clear left a gap with no tile at all.
    const MIN_IMPORT_FEEDBACK_MS = 650;
    const importingWorkspaceIds = new Set<string>();
    let importStartedAt = 0;

    const beginWorkspaceImport = (workspaceId: string) => {
      const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
      if (!normalizedWorkspaceId || importingWorkspaceIds.has(normalizedWorkspaceId)) return;
      if (importingWorkspaceIds.size === 0) importStartedAt = Date.now();
      importingWorkspaceIds.add(normalizedWorkspaceId);
      holdWorkspaceImport(normalizedWorkspaceId);
      readKernel().patchEntityRuntimeState('StudioWorkspace', normalizedWorkspaceId, {
        isImportingAssets: true,
      });
    };

    const clearWorkspaceImportFeedback = async () => {
      if (importingWorkspaceIds.size === 0) return;
      const remainingMs = MIN_IMPORT_FEEDBACK_MS - (Date.now() - importStartedAt);
      if (remainingMs > 0) await new Promise<void>((resolve) => { globalThis.setTimeout(resolve, remainingMs); });
      importingWorkspaceIds.forEach((workspaceId) => {
        // An overlapping drop into the same workspace is still uploading: its feedback stays.
        if (!releaseWorkspaceImport(workspaceId)) return;
        readKernel().patchEntityRuntimeState('StudioWorkspace', workspaceId, {
          isImportingAssets: undefined,
        });
      });
      importingWorkspaceIds.clear();
    };

    // Only a drop captured as PROJECT is published as a detached Project Layer. Canvas drops
    // remain workspace-owned even if the operator changes views during the upload.
    const publishImportedLayersAsListOutputs = async () => {
      if (importedLayers.length === 0 || capturedImportViewMode !== 'marketing') return;

      const projectOwnerIdForImports = readKernel().getInventory().Project.registry[originalProjectId]?.userId;
      const layerRegistry = readKernel().getLayerRegistry();

      let skippedNonVisualImport = false;
      for (const { layerId, sourceFile } of importedLayers) {
        const layer = layerRegistry[layerId];
        if (!layer) continue;
        // Audio has no still to show on a card, so it stays a layer rather
        // than becoming a tile that can never render.
        if (layer.type === 'audio') {
          skippedNonVisualImport = true;
          continue;
        }
        const layerWorkspaceId = layer.layerWorkspaceId?.trim();
        if (!layerWorkspaceId) continue;
        try {
          // The tile must show the imported file itself. Videos already carry an extracted
          // frame; a still may need its source uploaded before it is detached.
          const posterUrl = layer.videoFramesUrls?.[0]
            || layer.imageSrc
            || await uploadFileToCloud(
              sourceFile,
              `${Date.now()}-${sourceFile.name}`,
              sourceFile.type || 'image/png',
              undefined,
              projectOwnerIdForImports,
            );
          if (posterUrl) registerCloudAssets([posterUrl], 'upload');
          const projectLayer = materializeProjectGenerationLayer(
            posterUrl && !layer.imageSrc ? { ...layer, imageSrc: posterUrl } : layer,
            originalProjectId,
          );
          readKernel().detachLayerFromWorkspace(layerWorkspaceId, layerId);
          readKernel().setLayer(layerId, projectLayer);
        } catch (error) {
          console.error(`[Kernel.addFilesAsLayers] Failed to publish imported layer "${layerId}" as a List output.`, error);
          toast.error(`Couldn't add ${layer.name || 'the imported file'} to the list.`);
        }
      }
      // Silence here would read as a broken import, which is the exact bug this
      // whole path exists to remove.
      if (skippedNonVisualImport) {
        toast.info('A list holds images and videos only, so audio files were not added.');
      }
    };

    const redo = async () => {
      try {
        const imagesInChatInputToAdd: File[] = [];
        let newWorkspaceIdForThisDrop: string | null = null;
        let newWorkspaceForThisDrop: Workspace | null = null;

        const projectOwnerId = readKernel().getInventory().Project.registry[originalProjectId]?.userId;

        const unsupportedFileNames: string[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (!file) continue;
          const fileNameLower = file.name.toLowerCase();
          const fileType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
          const hasExt = (pattern: RegExp) => pattern.test(fileNameLower);
          const isVideoFile =
            fileType.startsWith('video/') ||
            hasExt(/\.(mp4|mov|webm|m4v|mkv|avi)$/i);
          const isAudioFile =
            fileType.startsWith('audio/') ||
            hasExt(/\.(mp3|wav|m4a|aac|ogg|oga|flac|opus|weba)$/i);
          const isImageFile =
            fileType.startsWith('image/') ||
            hasExt(/\.(png|jpe?g|webp|gif|bmp|svg)$/i);
          try {
            if (dropTargetArea === 'imagesInChatInput' && isImageFile) {
              imagesInChatInputToAdd.push(file);
            } else if (isVideoFile) {
              let targetWorkspaceId: string | null = normalizedTimelineDrop?.workspaceId ?? null;
              if (!targetWorkspaceId && worldPos) {
                const allWorkspaces = listProjectWorkspaces();
                for (const ws of [...allWorkspaces].reverse()) {
                  if (!(ws.visible ?? true)) continue;
                  const frame = ws.frameRect;
                  if (worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w && worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h) {
                    targetWorkspaceId = ws.id;
                    break;
                  }
                }
              }
              const finalWorkspaceId = targetWorkspaceId ?? readKernel().getProjectUIWorkspaceId(originalProjectId);
              beginWorkspaceImport(finalWorkspaceId);
              const uploadMimeType = fileType || 'video/mp4';
              const cloudUrl = await uploadFileToCloud(file, `${Date.now()}-${file.name}`, uploadMimeType, undefined, projectOwnerId);
              if (!cloudUrl) throw new Error('Upload failed, no URL.');
              registerCloudAssets([cloudUrl], 'upload');

              const newLayer = await readKernel().addLayerFromUrl(finalWorkspaceId, cloudUrl, { name: file.name.replace(/\.[^/.]+$/, "") || 'Dropped Video', source: 'user-drop' });
              applyTimelineDropPlacement(finalWorkspaceId, newLayer.id);
              registerImportedLayer(newLayer.id, file);
              toast.success(`Added video: ${file.name}`);

            } else if (isAudioFile) {
              let targetWorkspaceId: string | null = normalizedTimelineDrop?.workspaceId ?? null;
              if (!targetWorkspaceId && worldPos) {
                const allWorkspaces = listProjectWorkspaces();
                for (const ws of [...allWorkspaces].reverse()) {
                  if (!(ws.visible ?? true)) continue;
                  const frame = ws.frameRect;
                  if (worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w && worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h) {
                    targetWorkspaceId = ws.id;
                    break;
                  }
                }
              }
              const finalWorkspaceId = targetWorkspaceId ?? readKernel().getProjectUIWorkspaceId(originalProjectId);
              beginWorkspaceImport(finalWorkspaceId);
              const uploadMimeType = fileType || 'audio/mpeg';
              const cloudUrl = await uploadFileToCloud(file, `${Date.now()}-${file.name}`, uploadMimeType, undefined, projectOwnerId);
              if (!cloudUrl) throw new Error('Upload failed, no URL.');
              registerCloudAssets([cloudUrl], 'upload');

              const newLayer = await readKernel().addLayerFromUrl(finalWorkspaceId, cloudUrl, {
                name: file.name.replace(/\.[^/.]+$/, "") || 'Dropped Audio',
                source: 'user-drop',
                type: 'audio',
              });
              applyTimelineDropPlacement(finalWorkspaceId, newLayer.id);
              registerImportedLayer(newLayer.id, file);
              toast.success(`Added audio: ${file.name}`);
            } else if ((dropTargetArea === 'project' || dropTargetArea === 'timeline') && isImageFile) {
              if (normalizedTimelineDrop?.workspaceId) {
                const timelineWorkspaceId = normalizedTimelineDrop.workspaceId;
                beginWorkspaceImport(timelineWorkspaceId);
                const newLayer = await readKernel().addLayerFromFile(timelineWorkspaceId, file);
                applyTimelineDropPlacement(timelineWorkspaceId, newLayer.id);
                registerImportedLayer(newLayer.id, file);
                continue;
              }

              if (newWorkspaceIdForThisDrop) {
                beginWorkspaceImport(newWorkspaceIdForThisDrop);
                const newLayer = await readKernel().addLayerFromFile(newWorkspaceIdForThisDrop, file);
                applyTimelineDropPlacement(newWorkspaceIdForThisDrop, newLayer.id);
                registerImportedLayer(newLayer.id, file);
                continue;
              }

              let targetWorkspaceId: string | null = null;
              if (worldPos) {
                const allWorkspaces = listProjectWorkspaces();
                for (const ws of [...allWorkspaces].reverse()) {
                  if (!(ws.visible ?? true)) continue;
                  const frame = ws.frameRect;
                  if (worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w && worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h) {
                    targetWorkspaceId = ws.id;
                    break;
                  }
                }
              }

              if (targetWorkspaceId) {
                beginWorkspaceImport(targetWorkspaceId);
                const newLayer = await readKernel().addLayerFromFile(targetWorkspaceId, file);
                applyTimelineDropPlacement(targetWorkspaceId, newLayer.id);
                registerImportedLayer(newLayer.id, file);
              } else if (worldPos) {
                const getImageSize = (imgFile: File): Promise<{ width: number, height: number }> => {
                  return new Promise((resolve, reject) => {
                    const img = new Image();
                    const url = URL.createObjectURL(imgFile);
                    img.onload = () => {
                      resolve({ width: img.width, height: img.height });
                      URL.revokeObjectURL(url);
                    };
                    img.onerror = (err) => {
                      reject(err);
                      URL.revokeObjectURL(url);
                    };
                    img.src = url;
                  });
                };

                try {
                  const { width, height } = await getImageSize(file);
                  const allWorkspaces = listProjectWorkspaces();
                  // Workspace ids are global rows, so a per-project counter collides with other projects.
                  const newId = uuidv4().substring(0, 8);

                  const newWorkspace: Workspace = {
                    id: newId,
                    projectId: originalProjectId,
                    name: `Workspace ${allWorkspaces.length + 1}`,
                    layerIds: [],
                    activeVersionId: null,
                    frameRect: { x: worldPos.x - width / 2, y: worldPos.y - height / 2, w: width, h: height },
                    generationRect: { x: worldPos.x - width / 2, y: worldPos.y - height / 2, w: width, h: height },
                    annotationMask: null,
                    annotationMaskVersionId: null,
                    annotationsEnabled: true,
                    annotationOpaque: false,
                    expanded: true,
                    locked: false,
                    visible: true,
                    aspectRatio: null,
                    validatedVersionId: null
                  };

                  const mergedProject = readKernel().readProjectKernelMergedState(originalProjectId);
                  const existingIds = Array.isArray(mergedProject?.workspaceIds)
                    ? mergedProject.workspaceIds.filter((workspaceId): workspaceId is string => typeof workspaceId === 'string' && workspaceId.trim().length > 0)
                    : [];
                  if (!existingIds.includes(newWorkspace.id)) {
                    readKernel().mergeEntityKernelState('Project', originalProjectId, {
                      workspaceIds: [...existingIds, newWorkspace.id],
                    });
                  }
                  readKernel().setWorkspace(newWorkspace.id, newWorkspace, originalProjectId);
                  readKernel().setProjectUIWorkspaceId(originalProjectId, newWorkspace.id);
                  newWorkspaceIdForThisDrop = newWorkspace.id;
                  newWorkspaceForThisDrop = newWorkspace;
                  readKernel().persistWorkspaceCreate(newWorkspace, 'Kernel.addFilesAsLayers');

                  beginWorkspaceImport(newWorkspace.id);
                  const newLayer = await readKernel().addLayerFromFile(newWorkspace.id, file);
                  applyTimelineDropPlacement(newWorkspace.id, newLayer.id);
                  registerImportedLayer(newLayer.id, file);
                } catch (error) {
                  console.error("Error creating new workspace from dropped image:", error);
                  readKernel().handleError("Could not create workspace from image.");
                  const fallbackWorkspaceId = readKernel().getProjectUIWorkspaceId(originalProjectId);
                  beginWorkspaceImport(fallbackWorkspaceId);
                  const newLayer = await readKernel().addLayerFromFile(
                    fallbackWorkspaceId,
                    file
                  );
                  applyTimelineDropPlacement(fallbackWorkspaceId, newLayer.id);
                  registerImportedLayer(newLayer.id, file);
                }
              } else {
                const workspaceIdForImport = readKernel().getProjectUIWorkspaceId(originalProjectId);
                beginWorkspaceImport(workspaceIdForImport);
                const newLayer = await readKernel().addLayerFromFile(
                  workspaceIdForImport,
                  file
                );
                applyTimelineDropPlacement(workspaceIdForImport, newLayer.id);
                registerImportedLayer(newLayer.id, file);
              }
            } else {
              unsupportedFileNames.push(file.name);
            }
          } catch (error: any) {
            console.error(`Drop error (${file.name}):`, error);
            readKernel().handleError(`Failed process ${file.name}: ${error.message || 'Unknown error'}`);
          }
        }
        // A file no branch takes used to vanish without a word.
        if (unsupportedFileNames.length > 0) {
          toast.error(`Not added, this file type is not supported here: ${unsupportedFileNames.join(', ')}`);
        }
        if (imagesInChatInputToAdd.length > 0) {
          const { v4: uuidv4 } = await import('uuid');
          const lockableFiles: LockableFile[] = imagesInChatInputToAdd.map(f => ({ id: uuidv4(), file: f, locked: false }));
          readKernel().setImagesInChatInput((prev) => [...prev, ...lockableFiles]);
        }

        if (newWorkspaceForThisDrop && readKernel().zoomToFitRectInViewport) {
          readKernel().zoomToFitRectInViewport(originalProjectId, newWorkspaceForThisDrop.frameRect);
        }

        await publishImportedLayersAsListOutputs();
      } finally {
        await clearWorkspaceImportFeedback();
        readKernel().logUserAction('Files Added', { count: files.length, target: dropTargetArea });
      }
    };

    void redo();
  }, [kernel, registerCloudAssets]);

  const promptAndAddLayerFromFile = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,video/*,audio/*';
    input.onchange = (e) => {
      const target = e.target;
      const files = target instanceof HTMLInputElement ? target.files : null;
      if (files && files.length > 0) {
        addFilesAsLayers(files, 'project');
      }
    };
    input.click();
  }, [addFilesAsLayers]);

  // --- Getter Factory ---
  const generatedGetters = useMemo(() => {
    const kernelState = store.getState();
    
    const allKeys = [
      ...Object.keys(kernelState)
    ];

    return allKeys.reduce<Record<string, () => unknown>>((acc, key) => {
      const getterName = `get${key.charAt(0).toUpperCase() + key.slice(1)}`;
      Object.defineProperty(acc, getterName, {
        value: () => {
          // Check kernel store first, then leasing store
          const kState = store.getState();
          if (key in kState) return Reflect.get(kState, key);
          return undefined;
        },
        enumerable: true,
        configurable: true,
      });
      return acc;
    }, {}) as any;
  }, [store]);

  const getCurrentOrganisation = useCallback(() => {
    const state = store.getState();
    const orgId = state.userContext.organisationId;
    if (!orgId) return null;
    return state.inventory.Organisation.registry[orgId] || null;
  }, [store]);

  const getInventory = useCallback(() => {
    // Returns inventory merged with entityKernelState drafts.
    // Read-only convenience for UI. Do NOT use this as a source for registry writes.
    // For canonical write paths, read from getState().inventory instead.
    const state = store.getState();
    return withMergedInventory(state);
  }, [store]);

  const handleSocketReconnect = useCallback(async () => {
    try {
      await kernel.current?.reconnectInventory();
    } catch (error) {
      console.error('[Kernel] Failed to refresh leasing data after socket reconnect.', error);
    }
    // The socket was absent for a window; any terminal generation event emitted
    // during it was dropped (Socket.IO does not replay room events for absent
    // members). Reconcile long-running generations against server snapshots so a
    // result finalized while we were disconnected is recovered instead of lost.
    await Promise.all([
      (async () => {
        try {
          await kernel.current?.reconcilePendingGenerationTasks();
        } catch (error) {
          console.error('[Kernel] Failed to reconcile pending generation tasks after socket reconnect.', error);
        }
      })(),
      (async () => {
        try {
          await kernel.current?.resyncOpenStudioScene();
        } catch (error) {
          console.error('[Kernel] Failed to resync the open Studio scene after socket reconnect.', error);
        }
      })(),
    ]);
  }, [kernel]);

  useEffect(() => {
    const knownEntityTypes: EntityType[] = [...ENTITY_TYPES];
    const leasingEventTypes: LeasingEventType[] = ['CREATE', 'UPDATE', 'DELETE'];

    const isKnownEntityType = (value: string): value is EntityType =>
      knownEntityTypes.some((candidate) => candidate === value);

    const isLeasingEventType = (value: string): value is LeasingEventType =>
      leasingEventTypes.some((candidate) => candidate === value);

    const normaliseEvent = (raw: unknown): RealtimeEvent | null => {
      if (!isRecord(raw)) return null;
      const entityType =
        typeof raw.entityType === 'string' && isKnownEntityType(raw.entityType)
          ? raw.entityType
          : undefined;
      const eventType =
        typeof raw.eventType === 'string' && isLeasingEventType(raw.eventType)
          ? raw.eventType
          : undefined;
      const entityId = typeof raw.entityId === 'string' ? raw.entityId : undefined;

      if (!entityType || !eventType || !entityId) {
        return null;
      }

      const payload = isRecord(raw.payload)
        ? ({ ...raw.payload } as Record<string, unknown>)
        : ({} as Record<string, unknown>);

      if (eventType === 'CREATE') {
        return {
          entityType,
          eventType,
          entityId,
          payload,
          createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
          userId: typeof raw.userId === 'string' ? raw.userId : undefined,
          organisationId: typeof raw.organisationId === 'string' ? raw.organisationId : undefined
        } as unknown as RealtimeEvent;
      }

      return {
        entityType,
        eventType,
        entityId,
        payload,
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
        userId: typeof raw.userId === 'string' ? raw.userId : undefined,
        organisationId: typeof raw.organisationId === 'string' ? raw.organisationId : undefined
      } as RealtimeEvent;
    };

    const normalisePayload = (raw: unknown): RealtimeEvent[] => {
      if (Array.isArray(raw)) {
        return raw
          .map((item) => normaliseEvent(item))
          .filter((event): event is RealtimeEvent => Boolean(event));
      }

      const event = normaliseEvent(raw ?? {});
      return event ? [event] : [];
    };

    const handleLeasingEvents = (raw: unknown) => {
      const events = normalisePayload(raw);
      if (events.length === 0) {
        return;
      }

      const latestTimestamp = events[events.length - 1]?.createdAt;
      if (latestTimestamp) {
        const currentTimestamp = kernel.current?.getSocketLastEventTimestamp();
        if (currentTimestamp !== latestTimestamp) {
          kernel.current?.setSocketLastEventTimestamp(latestTimestamp);
        }
      }

      // 1. Apply state updates to the store first.
      kernel.current?.applyInventoryEvents(events);

      // 2. Run campaign mission logic outside the synchronous socket update stack
      // to avoid nested store-update cascades when completion handlers trigger refreshes.
      queueMicrotask(() => {
        if (!kernel.current) return;
        events.forEach((event) => {
          if (
            event.entityType === 'SourcingCampaign'
            || event.entityType === 'PortfolioFitCampaign'
          ) {
            kernel.current?.handleCampaignEvent(event);
          }
        });
      });
    };

    return subscribeToSocketEvent(LEASING_EVENT_NAME, handleLeasingEvents);
  }, [kernel, subscribeToSocketEvent]);

  const getLeasingBrandMap = useCallback(() => store.getState().inventory.Brand.registry, [store]);
  const getLeasingBrandOrder = useCallback(() => {
    const registry = store.getState().inventory.Brand.registry;
    return Object.keys(registry).sort((leftId, rightId) => {
      const leftName = (registry[leftId]?.name || '').toLowerCase();
      const rightName = (registry[rightId]?.name || '').toLowerCase();
      return leftName.localeCompare(rightName);
    });
  }, [store]);
  const getLeasingDealMap = useCallback(() => store.getState().inventory.Deal.registry, [store]);
  const getWorkspaceRegistry = useCallback((): Record<string, Workspace> => {
    const state = store.getState();
    const mergedInventory = withMergedInventory(state);
    const registry: Record<string, Workspace> = {};
    Object.keys(mergedInventory.StudioWorkspace.registry).forEach((workspaceId) => {
      const workspace = mergedInventory.StudioWorkspace.registry[workspaceId];
      if (workspace) {
        registry[workspaceId] = toWorkspaceState(workspace);
      }
    });
    return registry;
  }, [store]);

  const getWorkspace = useCallback((workspaceId: string | null | undefined): Workspace | null => {
    if (!workspaceId) return null;
    return getWorkspaceRegistry()[workspaceId] ?? null;
  }, [getWorkspaceRegistry]);

  const getLayerRegistry = useCallback((): Record<string, Layer> => {
    const state = store.getState();
    const mergedInventory = withMergedInventory(state);
    const registry: Record<string, Layer> = {};
    Object.keys(mergedInventory.StudioLayer.registry).forEach((layerId) => {
      const layer = mergedInventory.StudioLayer.registry[layerId];
      if (!layer) return;
      registry[layerId] = toLayerState(layer);
    });
    return registry;
  }, [store]);

  const setWorkspace = useCallback<KernelHandle['setWorkspace']>((workspaceId, updater, projectIdInput) => {
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!normalizedWorkspaceId) {
      throw new Error('[Kernel.setWorkspace] workspaceId is required.');
    }
    const normalizedProjectId = typeof projectIdInput === 'string' ? projectIdInput.trim() : '';
    if (!normalizedProjectId) {
      throw new Error(`[Kernel.setWorkspace] projectId is required for workspace "${normalizedWorkspaceId}".`);
    }

    const mergedInventory = withMergedInventory(store.getState());
    const scopedProject = mergedInventory.Project.registry[normalizedProjectId];
    if (!scopedProject) {
      throw new Error(
        `[Kernel.setWorkspace] Project "${normalizedProjectId}" not found for workspace "${normalizedWorkspaceId}".`
      );
    }

    const resolvedWorkspaceId = normalizedWorkspaceId;
    const previousWorkspaceRaw = mergedInventory.StudioWorkspace.registry[resolvedWorkspaceId];
    if (previousWorkspaceRaw) {
      const previousProjectId =
        typeof previousWorkspaceRaw.projectId === 'string' ? previousWorkspaceRaw.projectId.trim() : '';
      if (!previousProjectId) {
        throw new Error(
          `[Kernel.setWorkspace] Workspace "${resolvedWorkspaceId}" has no canonical project scope.`
        );
      }
      if (previousProjectId !== normalizedProjectId) {
        throw new Error(
          `[Kernel.setWorkspace] Project scope mismatch for workspace "${resolvedWorkspaceId}": received "${normalizedProjectId}", expected "${previousProjectId}".`
        );
      }
    }
    const projectWorkspaceIds = Array.isArray(scopedProject.workspaceIds)
      ? scopedProject.workspaceIds
      : [];
    if (!projectWorkspaceIds.includes(resolvedWorkspaceId)) {
      throw new Error(
        `[Kernel.setWorkspace] Workspace "${resolvedWorkspaceId}" is outside project "${normalizedProjectId}" scope. Add it to Project.workspaceIds before mutating.`
      );
    }

    const previousWorkspace: Workspace | null = previousWorkspaceRaw
      ? { ...toWorkspaceState(previousWorkspaceRaw) }
      : null;

    const nextWorkspaceRaw = typeof updater === 'function'
      ? (previousWorkspace ? updater(previousWorkspace) : null)
      : updater;

    if (!nextWorkspaceRaw) {
      const rawRegistry = store.getState().inventory.StudioWorkspace.registry;
      const hasRawEntity = Boolean(rawRegistry[resolvedWorkspaceId]);

      entityKernelStateManager.removeEntityKernelState('StudioWorkspace', resolvedWorkspaceId);
      if (hasRawEntity) {
        entityKernelStateManager.mergeEntityKernelState('StudioWorkspace', resolvedWorkspaceId, {
          [LOCAL_ENTITY_DELETE_MARKER]: true,
        });
      }
      return;
    }

    const nextWorkspace: Workspace = {
      ...nextWorkspaceRaw,
      id: resolvedWorkspaceId,
      projectId: normalizedProjectId,
      layerIds: toNormalizedStringArray(nextWorkspaceRaw.layerIds),
    };

    const existingDto = store.getState().inventory.StudioWorkspace.registry[resolvedWorkspaceId];
    const versionRegistry = store.getState().inventory.StudioWorkspaceVersion.registry;
    const dto = toStudioWorkspaceDtoFromRuntime(
      nextWorkspace,
      existingDto,
      normalizedProjectId,
      new Date().toISOString()
    );
    const runtime = toWorkspaceRuntimeOverlay(nextWorkspace);
    const nextAutosavePayload = toStudioWorkspaceAutosavePayload(dto);
    let previousAutosavePayload: StudioWorkspaceAutosavePayload | null = null;
    if (previousWorkspace) {
      try {
        const previousDto = toStudioWorkspaceDtoFromRuntime(
          previousWorkspace,
          existingDto,
          normalizedProjectId,
          existingDto?.updatedAt ?? new Date().toISOString(),
        );
        previousAutosavePayload = toStudioWorkspaceAutosavePayload(previousDto);
      } catch {
        previousAutosavePayload = null;
      }
    }
    const workspaceVersionsAutosavePayload = buildWorkspaceVersionAutosavePayload({
      workspaceId: resolvedWorkspaceId,
      projectId: normalizedProjectId,
      previousWorkspace,
      nextWorkspace,
      versionRegistry,
    });

    let currentDraft = entityKernelStateManager.readEntityKernelState('StudioWorkspace', resolvedWorkspaceId);
    if (isRecord(currentDraft) && currentDraft[LOCAL_ENTITY_DELETE_MARKER] === true) {
      entityKernelStateManager.removeEntityKernelState('StudioWorkspace', resolvedWorkspaceId);
      currentDraft = null;
    }

    // Preserve non-DTO runtime keys (notably autosave metadata) across DTO writes.
    const preservedRuntime =
      isRecord(currentDraft) && isRecord(currentDraft.runtime)
        ? (currentDraft.runtime as Record<string, unknown>)
        : {};
    entityKernelStateManager.writeEntityKernelState('StudioWorkspace', resolvedWorkspaceId, {
      ...dto,
      runtime: {
        ...preservedRuntime,
        ...runtime,
      },
    });

    // Only the fields this write changed, diffed against the workspace the caller read: an unchanged
    // field sent back overwrites what another writer changed since.
    const autosavePayload: Partial<StudioWorkspaceAutosavePayload> = previousAutosavePayload
      ? Object.fromEntries(Object.entries(nextAutosavePayload).filter(([key, value]) => !deepEqual(value, Reflect.get(previousAutosavePayload, key))))
      : nextAutosavePayload;
    // With neither row nor draft this is a creation or a restore, which its caller sends itself: none of it is a
    // change the engine should send over what the row shows first.
    if (!existingDto && !previousWorkspace) {
      inventoryManager.markStudioWorkspaceFieldsSent(resolvedWorkspaceId, nextAutosavePayload);
    }
    // No UPDATE while the row is absent, it would race the CREATE: the engine queues the draft's residual once the row shows.
    if (existingDto && Object.keys(autosavePayload).length > 0) {
      inventoryManager.queueStudioWorkspaceSave(resolvedWorkspaceId, normalizedProjectId, autosavePayload);
    }
    if (workspaceVersionsAutosavePayload) {
      inventoryManager.queueAutosave<StudioWorkspaceVersionAutosavePayload>({
        entityType: 'StudioWorkspace',
        entityId: resolvedWorkspaceId,
        scope: STUDIO_WORKSPACE_VERSIONS_AUTOSAVE_SCOPE,
        reason: 'StudioWorkspaceVersion DTO autosave',
        debounceMs: STUDIO_WORKSPACE_VERSIONS_AUTOSAVE_DEBOUNCE_MS,
        maxRetries: 0,
        buildPayload: () => workspaceVersionsAutosavePayload,
        mergePending: mergeWorkspaceVersionAutosavePayloads,
        persist: async (payload) => {
          const operations = [
            ...payload.upserts.map((upsert) => ({
              action: upsert.action,
              entity: 'StudioWorkspaceVersion' as const,
              id: upsert.id,
              payload: { ...upsert.payload } as Record<string, unknown>,
            })),
            ...payload.detachIds.map((versionId) => ({
              action: 'UPDATE' as const,
              entity: 'StudioWorkspaceVersion',
              id: versionId,
              payload: {
                workspaceId: null,
              },
            })),
          ];
          if (operations.length === 0) return true;
          await inventoryManager.queueStudioWrite(
            operations as any,
            { batch: true }
          );
          return true;
        },
      });
    }
  }, [entityKernelStateManager, inventoryManager, store]);

  // A raster layer's image leaves with its rect, scale and contentVersion, all four from one read of the
  // draft: the row never pairs a geometry with another state's image. Pixels the draft holds unstored are
  // uploaded here, before queueStudioWrite, whose one request in flight serves the whole Studio.
  const queueLayerSave = useCallback<KernelHandle['queueLayerSave']>((layerId, changes) => {
    inventoryManager.queueAutosave<Partial<StudioLayerAutosavePayload>>({
      entityType: 'StudioLayer',
      entityId: layerId,
      scope: STUDIO_LAYER_DTO_AUTOSAVE_SCOPE,
      reason: 'StudioLayer DTO autosave',
      debounceMs: STUDIO_LAYER_DTO_AUTOSAVE_DEBOUNCE_MS,
      maxRetries: 0,
      buildPayload: () => changes,
      mergePending: (pending, next) => ({ ...pending, ...next }),
      fieldDeltas: true,
      persist: async (payload) => {
        const readLayer = () => {
          const raw = withMergedInventory(store.getState()).StudioLayer.registry[layerId];
          return raw ? toLayerState(raw) : null;
        };
        const read = readLayer();
        const uploads = read !== null && hasUnstoredLayerPixels(read);
        const layer = !uploads ? read : await inventoryManager.prepareStudioWrite((async () => {
          let state: Layer | null = read;
          while (state && hasUnstoredLayerPixels(state)) {
            const uploaded: Layer = state;
            const ownerId = store.getState().inventory.Project.registry[uploaded.projectId ?? '']?.userId;
            const imageSrc = await uploadImageDataToCloud(uploaded.imageData!, 'webp', undefined, ownerId).catch(() => null);
            if (!imageSrc?.startsWith('/api/')) {
              kernel.current?.handleError(`Layer "${uploaded.name || layerId}" could not be saved: its pixels only exist in this tab. Keep the tab open and export it before reloading.`, {
                id: `layer-save-failed-${layerId}`,
                logToConsole: false,
              });
              throw new Error(LAYER_PIXELS_UPLOAD_FAILED);
            }
            state = readLayer();
            // An undo or a newer edit replaced these pixels meanwhile: their image is never written, the state now shown is.
            if (state?.imageData === uploaded.imageData) {
              entityKernelStateManager.mergeEntityKernelState('StudioLayer', layerId, { imageSrc });
              state = { ...uploaded, imageSrc };
            }
          }
          return state;
        })());
        const hasRow = Boolean(store.getState().inventory.StudioLayer.registry[layerId]);
        let sent: Record<string, unknown> = payload;
        if (!layer) {
          // Removed: its image and geometry are no longer known together, and the row keeps its own.
          sent = Object.fromEntries(Object.entries(payload).filter(([key]) => !RASTER_GROUP_KEYS.has(key)));
          if (!hasRow || Object.keys(sent).length === 0) return true;
        } else if (isRasterLayer(layer) && (uploads || 'imageSrc' in payload)) {
          sent = { ...payload, imageSrc: layer.imageSrc ?? null, rect: layer.rect, scale: layer.scale ?? null, contentVersion: layer.contentVersion ?? null };
        }
        await inventoryManager.queueStudioWrite(
          [{ action: hasRow ? 'UPDATE' : 'CREATE', entity: 'StudioLayer', id: layerId, payload: sent } as any],
          { batch: true },
        );
        return { sent };
      },
    });
  }, [entityKernelStateManager, inventoryManager, kernel, store]);

  const setLayer = useCallback<KernelHandle['setLayer']>((layerId, updater) => {
    const normalizedLayerId = typeof layerId === 'string' ? layerId.trim() : '';
    if (!normalizedLayerId) {
      throw new Error('[Kernel.setLayer] layerId is required.');
    }

    const mergedInventory = withMergedInventory(store.getState());
    const resolvedLayerId = normalizedLayerId;
    const previousLayerRaw = mergedInventory.StudioLayer.registry[resolvedLayerId];

    const previousLayer: Layer | null = previousLayerRaw ? toLayerState(previousLayerRaw) : null;

    const nextLayerRaw = typeof updater === 'function'
      ? (previousLayer ? updater(previousLayer) : null)
      : updater;

    const previousWorkspaceId = getLayerWorkspaceId(previousLayer ?? { projectId: '' });
    const existingDto = store.getState().inventory.StudioLayer.registry[resolvedLayerId];

    if (!nextLayerRaw) {
      const rawRegistry = store.getState().inventory.StudioLayer.registry;
      const hasRawEntity = Boolean(rawRegistry[resolvedLayerId]);

      if (!hasRawEntity) inventoryManager.cancelAutosave('StudioLayer', resolvedLayerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE);
      entityKernelStateManager.removeEntityKernelState('StudioLayer', resolvedLayerId);
      if (hasRawEntity) {
        entityKernelStateManager.mergeEntityKernelState('StudioLayer', resolvedLayerId, {
          [LOCAL_ENTITY_DELETE_MARKER]: true,
        });
      }
      return;
    }

    const nextLayer: Layer = {
      ...nextLayerRaw,
      id: resolvedLayerId,
      projectId:
        typeof nextLayerRaw.projectId === 'string' && nextLayerRaw.projectId.trim().length > 0
          ? nextLayerRaw.projectId.trim()
          : (typeof existingDto?.projectId === 'string' ? existingDto.projectId.trim() : ''),
      layerWorkspaceId:
        nextLayerRaw.layerWorkspaceId === undefined
          ? previousWorkspaceId
          : normalizeLayerWorkspaceId(nextLayerRaw.layerWorkspaceId),
      'top-layer': nextLayerRaw['top-layer'] === true,
    };

    const nextWorkspaceId = getLayerWorkspaceId(nextLayer);
    const workspaceRow = nextWorkspaceId
      ? mergedInventory.StudioWorkspace.registry[nextWorkspaceId]
      : undefined;
    const workspaceProjectId =
      typeof workspaceRow?.projectId === 'string' ? workspaceRow.projectId.trim() : '';
    const resolvedProjectId = nextLayer.projectId?.trim() || workspaceProjectId;
    if (!resolvedProjectId) {
      throw new Error(`[Kernel.setLayer] Missing explicit project scope for layer "${resolvedLayerId}".`);
    }
    if (nextWorkspaceId) {
      // Layer DTOs can arrive before a partial workspace relation is hydrated. The layer's
      // projectId is still an explicit scope; only reject a workspace when its known scope
      // contradicts that layer, not when the relation is temporarily incomplete.
      if (workspaceProjectId && workspaceProjectId !== resolvedProjectId) {
        throw new Error(
          `[Kernel.setLayer] Project scope mismatch for layer "${resolvedLayerId}": layer is "${resolvedProjectId}" but workspace "${nextWorkspaceId}" is "${workspaceProjectId}".`
        );
      }
    }
    nextLayer.projectId = resolvedProjectId;
    let dto: StudioLayerDTO;
    try {
      dto = toStudioLayerDtoFromRuntime(
        nextLayer,
        existingDto,
        resolvedProjectId,
        new Date().toISOString()
      );
    } catch (error) {
      throw new Error(
        `[Kernel.setLayer] Failed to build DTO for layer "${resolvedLayerId}" with explicit scope.`
      );
    }
    const runtime = toLayerRuntimeOverlay(nextLayer);
    const nextAutosavePayload = toStudioLayerAutosavePayload(dto);
    let previousAutosavePayload: StudioLayerAutosavePayload | null = null;
    if (previousLayer) {
      try {
        const previousDto = toStudioLayerDtoFromRuntime(
          previousLayer,
          existingDto,
          resolvedProjectId,
          existingDto?.updatedAt ?? new Date().toISOString(),
        );
        previousAutosavePayload = toStudioLayerAutosavePayload(previousDto);
      } catch {
        previousAutosavePayload = null;
      }
    }

    let currentDraft = entityKernelStateManager.readEntityKernelState('StudioLayer', resolvedLayerId);
    if (isRecord(currentDraft) && currentDraft[LOCAL_ENTITY_DELETE_MARKER] === true) {
      entityKernelStateManager.removeEntityKernelState('StudioLayer', resolvedLayerId);
      currentDraft = null;
    }

    // Preserve non-DTO runtime keys (notably autosave metadata) across DTO writes.
    const preservedRuntime =
      isRecord(currentDraft) && isRecord(currentDraft.runtime)
        ? (currentDraft.runtime as Record<string, unknown>)
        : {};
    entityKernelStateManager.writeEntityKernelState('StudioLayer', resolvedLayerId, {
      ...dto,
      runtime: {
        ...preservedRuntime,
        ...runtime,
      },
    });

    const hasCanonicalEntity = Boolean(store.getState().inventory.StudioLayer.registry[resolvedLayerId]);
    // Only the fields this write changed: an unchanged field sent back overwrites what another writer
    // changed since. Diffed against the layer the caller read, never the row, which still matches the
    // revert of a change in flight.
    const deltaBase = hasCanonicalEntity ? previousAutosavePayload : null;
    const autosavePayload: Partial<StudioLayerAutosavePayload> = deltaBase
      ? Object.fromEntries(Object.entries(nextAutosavePayload).filter(([key, value]) => !deepEqual(value, Reflect.get(deltaBase, key))))
      : nextAutosavePayload;
    const shouldQueueDtoAutosave =
      Object.keys(autosavePayload).length > 0 &&
      !areAutosavePayloadsEqual(previousAutosavePayload, nextAutosavePayload) &&
      (hasCanonicalEntity || !isProjectLayer(nextLayer) || previousWorkspaceId !== null
        || inventoryManager.getAutosaveState('StudioLayer', resolvedLayerId, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE).dirty);
    if (shouldQueueDtoAutosave) queueLayerSave(resolvedLayerId, autosavePayload);
  }, [entityKernelStateManager, inventoryManager, queueLayerSave, store]);

  const setActiveToolWithWorkspaceAnnotations = useCallback((updater: React.SetStateAction<InpaintTool>) => {
    const state = store.getState();
    const previousTool = state.activeTool;
    const nextTool = typeof updater === 'function'
      ? updater(previousTool)
      : updater;

    const isAnnotationTool =
      nextTool === 'annotationLine' ||
      nextTool === 'annotationEraser' ||
      nextTool === 'comment';

    if (isAnnotationTool) {
      const projectId = kernel.current?.resolveActiveSessionProjectId?.() || '';
      const activeWorkspaceId = projectId
        ? workspaceManager.getProjectUIWorkspaceId(projectId)
        : '';
      if (activeWorkspaceId) {
        kernel.current?.setWorkspace(activeWorkspaceId, (workspace) =>
          workspace.annotationsEnabled === false
            ? { ...workspace, annotationsEnabled: true }
            : workspace
        , projectId);
      }
    }

    setters.setActiveTool(nextTool);
  }, [workspaceManager, kernel, setters, store]);

  const { getState, setState: setStoreState, subscribe } = store;
  const setState = useCallback<KernelHandle['setState']>((partial, replace) => {
    if (replace === true) {
      setStoreState((state) => {
        const resolvedPartial = typeof partial === 'function' ? partial(state) : partial;
        if (!resolvedPartial || typeof resolvedPartial !== 'object') return state;
        return { ...state, ...resolvedPartial };
      }, true);
      return;
    }

    if (typeof partial === 'function') {
      setStoreState((state) => {
        const nextPartial = partial(state);
        return (!nextPartial || typeof nextPartial !== 'object') ? nextPartial : nextPartial;
      });
      return;
    }
    setStoreState(partial);
  }, [setStoreState]);

  const contextValue: KernelHandle = useMemo(() => ({
    kernel,
    kernelReady,
    ...generatedGetters,
    getInventory,
    getWorkspace,
    setLeftPanelRef,
    setRightPanelRef,
    toggleLeftPanel,
    openLeftPanel,
    toggleRightPanel,
    openRightPanel,
    ...setters,
    setActiveTool: setActiveToolWithWorkspaceAnnotations,
    ...commands,
    ...audioManager,
    ...projectManager,
    ...workspaceManager,
    ...layerManager,
    ...agentManager,
    ...zoomManager,
    ...viewportManager,
    ...canvasManager,
    ...modelManager,
    ...taskManager,
    ...versionManager,
    ...workspaceTimelineManager,
    ...studioDevelopManager,
    ...chatManager,
    ...inventoryManager,
    ...scoringCampaignsManager,
    ...leasingWorkflowsManager,
    ...teamsManager,
    ...entityKernelStateManager,
    canvasRef,
    consecutiveRevertCounterRef,
    pendingTaskPromisesRef: taskManager.pendingTaskPromisesRef,
    pendingLayerPromisesRef,
    canvasContainerRef,
    videoRefs,
    blendWorkerRef,
    generationPostprocessWorkerRef,
    buriedLayersWorkerRef,
    opencvWorkerRef,
    createOpenCvWorker,
    loadUserPreferences,
    saveUserPreferences,
    updateAndSaveUserPreference,
    saveDailyCreditUsageAlert,
    handleNotEnoughCreditsError,
    logUserAction,
    beginSourceAction,
    runGeneratedScriptLogic,
    parseSlashCommand,
    execute,
    executeSlashCommandDirectly,
    openModal,
    openImageCropForLockableFiles,
    closeModal,
    handleError,
    toggleReferenceImageLock,
    registerCloudAssets,
    clearAssetLoadingFailsafe,
    startAssetLoadingFailsafe,
    handleAssetLoadingTimeout,
    reportAssetLoaded,
    initializeVisualCriticalPath,
    refreshOrganisationDetails,
    fetchOrganisationPendingInvites,
    saveOrganisationSettings,
    inviteOrganisationMember,
    createOrganisationMemberDirectly,
    resendOrganisationInvite,
    deleteOrganisationInvite,
    uploadOrganisationLogo,
    applyHomeOrganisationScope,
    sendUserTeamReport,
    subscribeToSocketEvent,
    dispatchSocketEvent,
    handleSocketReconnect,
    getLeasingBrandMap,
    getLeasingBrandOrder,
    getLeasingDealMap,
    getCurrentOrganisation,
    executeAction,
    undoLastAction,
    redoLastAction,
    addFilesAsLayers,
    promptAndAddLayerFromFile,
    triggerGenerate,
    connectSocket,
    disconnectSocket,
    getSocketConnectionId,
    setActiveModal,
    setModalPayload,
    setIsGuestMode,
    setUndoStack,
    setRedoStack,
    getLayerRegistry,
    setWorkspace,
    setLayer,
    queueLayerSave,
    getState,
    setState,
    subscribe
  }), [
    kernel, kernelReady, loadUserPreferences, saveUserPreferences, updateAndSaveUserPreference, saveDailyCreditUsageAlert, chatManager, handleNotEnoughCreditsError,
    logUserAction, beginSourceAction, runGeneratedScriptLogic, parseSlashCommand, execute, executeSlashCommandDirectly, openModal, openImageCropForLockableFiles, closeModal, clearAssetLoadingFailsafe,
    startAssetLoadingFailsafe, handleAssetLoadingTimeout, reportAssetLoaded, initializeVisualCriticalPath,
    subscribeToSocketEvent, dispatchSocketEvent, handleSocketReconnect, audioManager, connectSocket, disconnectSocket, getSocketConnectionId,
    projectManager, workspaceManager, layerManager, agentManager, zoomManager, viewportManager, canvasManager, modelManager,
    getState, setState, subscribe, getWorkspace, getLayerRegistry, setWorkspace, setLayer, queueLayerSave,
    handleError, executeAction, undoLastAction, redoLastAction,
    getLeasingBrandMap, getLeasingBrandOrder,
    getCurrentOrganisation, addFilesAsLayers, promptAndAddLayerFromFile, triggerGenerate, registerCloudAssets,
    setters, setActiveModal, setModalPayload, setIsGuestMode, commands,
    setLeftPanelRef, setRightPanelRef, toggleLeftPanel, openLeftPanel, toggleRightPanel, openRightPanel,
    setUndoStack, setRedoStack,
    toggleReferenceImageLock, taskManager, versionManager, workspaceTimelineManager, studioDevelopManager,
    inventoryManager, scoringCampaignsManager, getLeasingDealMap,
    refreshOrganisationDetails, fetchOrganisationPendingInvites, saveOrganisationSettings, inviteOrganisationMember,
    createOrganisationMemberDirectly, resendOrganisationInvite, deleteOrganisationInvite, uploadOrganisationLogo,
    applyHomeOrganisationScope, sendUserTeamReport, generatedGetters, leasingWorkflowsManager, teamsManager,
    getInventory, setActiveToolWithWorkspaceAnnotations,
    entityKernelStateManager
  ]);

  useEffect(() => {
    kernel.current = contextValue;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setKernelReady(true);
    // Dev-only: expose the kernel handle for deterministic verification/debugging
    // (e.g. driving Master command runs from the console). Never in production.
    if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
      (window as unknown as { __faterKernel?: KernelHandle }).__faterKernel = contextValue;
    }
  }, [kernel, contextValue]);

  return (
    <Kernel.Provider value={contextValue}>
      {children}
    </Kernel.Provider>
  );
};
